import { getLang, t } from './i18n';

// In local dev (CRA dev server on :3000) we call the separate backend on :5000.
// In production the backend serves the built frontend from the same origin, so
// the build sets REACT_APP_API_URL='' and requests use relative paths
// (/api/..., /uploads/...). An explicit empty string must be respected, hence
// the `!== undefined` check rather than `||` (which treats '' as falsy).
const API_URL =
  process.env.REACT_APP_API_URL !== undefined
    ? process.env.REACT_APP_API_URL
    : 'http://localhost:5000';

// Build full image URL from relative path
export const imageUrl = (relativePath) => {
  if (!relativePath) return null;
  if (relativePath.startsWith('http')) return relativePath;
  return `${API_URL}${relativePath}`;
};

// Zmanim / Shabbat times — routed through our backend proxy (Chabad-sourced via
// zman.ms5.uk, Hebcal fallback) instead of hitting Hebcal directly. The backend
// returns the SAME shape Hebcal did — `{times:{sunset,plagHaMincha,tzeit85deg}}`
// and `{items:[{category,date,title}]}` — so callers keep their existing parsing.
// No date param: the backend derives "today" in the place's timezone from the
// SERVER clock, so a wrong device clock can't shift which day's zmanim show.
export const zmanimUrl = (lat, lng, tz) =>
  `${API_URL}/api/zmanim?latitude=${lat}&longitude=${lng}&tzid=${encodeURIComponent(tz)}`;
export const shabbatUrl = (lat, lng, tz) =>
  `${API_URL}/api/shabbat?latitude=${lat}&longitude=${lng}&tzid=${encodeURIComponent(tz)}`;

// ---- Authoritative clock (server-synced) ----
// A wrong/unset device clock would break "which minyan is next" and other
// current-time math. Fetch the server's instant once, remember the offset from
// the device clock, and apply it in serverNowMs().
let _clockOffsetMs = 0;
export async function syncServerClock() {
  try {
    const t0 = Date.now();
    const r = await fetch(`${API_URL}/api/now`, { cache: 'no-store' });
    if (!r.ok) return;
    const { epoch } = await r.json();
    const rtt = Date.now() - t0;
    // Server "now" as of when we received it (+ half the round-trip) minus ours.
    if (typeof epoch === 'number') _clockOffsetMs = (epoch + rtt / 2) - Date.now();
  } catch (e) { /* offline / blocked → fall back to the device clock */ }
}
export const serverNowMs = () => Date.now() + _clockOffsetMs;

// Current { day (0=Sun … 6=Sat), minutes (since midnight) } in an IANA tz, from
// the authoritative instant — so "next minyan" is judged in the PLACE's
// timezone, not the viewer's device tz.
const _WEEKDAY_IDX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
export function nowPartsInTz(tzid) {
  const d = new Date(serverNowMs());
  try {
    const p = new Intl.DateTimeFormat('en-US', { timeZone: tzid || 'UTC', weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false, month: 'numeric' })
      .formatToParts(d).reduce((o, x) => { o[x.type] = x.value; return o; }, {});
    const day = _WEEKDAY_IDX[p.weekday] ?? d.getDay();
    const hour = parseInt(p.hour, 10) % 24;
    const minute = parseInt(p.minute, 10);
    const month = parseInt(p.month, 10) || (d.getMonth() + 1);
    return { day, minutes: (Number.isFinite(hour) ? hour : 0) * 60 + (Number.isFinite(minute) ? minute : 0), month };
  } catch (e) {
    return { day: d.getDay(), minutes: d.getHours() * 60 + d.getMinutes(), month: d.getMonth() + 1 };
  }
}

// --- "Open now" ------------------------------------------------------------
// Work out whether a place is open right now from its structured opening_hours
// ({ schedule: [{ days:['mon',…], ranges:[{open:'09:00',close:'17:00'}],
// closed, note }] }). Uses the place's LOCAL time (its timezone, from coords/
// country) off the server-synced clock, and handles overnight ranges (close ≤
// open spills into the next morning) plus a "closing soon" flag. Returns null
// when there's no usable structured schedule (caller shows nothing).
const _DAY_CODES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const _hhmm = (s) => { const m = /^(\d{1,2}):(\d{2})$/.exec(String(s || '')); return m ? (parseInt(m[1], 10) * 60 + parseInt(m[2], 10)) : null; };

// --- Zmanim, computed locally ----------------------------------------------
// Fifteen places write their motzei-Shabbat opening as "shabbat_end" rather
// than a clock time. openStatus only understood HH:MM, so it silently dropped
// those ranges: fourteen restaurants read "Closed" all Saturday night, and one
// (whose separate `daily` rule covered Saturday) read OPEN during Shabbat.
//
// PrayerTimes resolves these tokens by fetching zmanim, but openStatus is
// synchronous and called while rendering a list of hundreds of cards, so it
// computes them instead. The sun's position is arithmetic; no network needed.
//
// The conventions match the site's own zmanim service (routes/zmanim.js asks
// Hebcal with b=18 and M=on):
//     sundown          sun at -0.833°  (standard sunset, incl. refraction)
//     candle_lighting  sundown - 18 min
//     shabbat_end      sun at -8.5°    (Hebcal's M=on havdalah)
//     plag_hamincha    1.25 seasonal hours before sundown
//
// NOAA solar equations. Returns a UTC epoch ms, or null in polar latitudes
// where the sun never reaches that altitude — the caller then simply skips the
// range, which is the old behaviour and no worse.
function _sunEventMs(y, mo, d, lat, lng, altDeg, rising) {
  const rad = Math.PI / 180;
  const a = Math.floor((14 - mo) / 12);
  const yy = y + 4800 - a;
  const mm = mo + 12 * a - 3;
  const jdn = d + Math.floor((153 * mm + 2) / 5) + 365 * yy + Math.floor(yy / 4)
    - Math.floor(yy / 100) + Math.floor(yy / 400) - 32045;
  const n = jdn - 2451545 + 0.0008;
  const Jstar = n - lng / 360;
  const M = (357.5291 + 0.98560028 * Jstar) % 360;
  const C = 1.9148 * Math.sin(M * rad) + 0.02 * Math.sin(2 * M * rad) + 0.0003 * Math.sin(3 * M * rad);
  const lam = (M + C + 180 + 102.9372) % 360;
  const Jtransit = 2451545 + Jstar + 0.0053 * Math.sin(M * rad) - 0.0069 * Math.sin(2 * lam * rad);
  const decl = Math.asin(Math.sin(lam * rad) * Math.sin(23.4397 * rad));
  const cosW = (Math.sin(altDeg * rad) - Math.sin(lat * rad) * Math.sin(decl))
    / (Math.cos(lat * rad) * Math.cos(decl));
  if (!(cosW >= -1 && cosW <= 1)) return null;
  const w = Math.acos(cosW) / rad;
  const J = rising ? Jtransit - w / 360 : Jtransit + w / 360;
  return (J - 2440587.5) * 86400000;
}

// An epoch ms → minutes from midnight in `tz`. Going through Intl keeps DST
// correct without this code knowing anything about offsets.
function _msToMinutesInTz(ms, tz) {
  try {
    const p = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false })
      .formatToParts(new Date(ms)).reduce((o, x) => { o[x.type] = x.value; return o; }, {});
    const h = parseInt(p.hour, 10); const m = parseInt(p.minute, 10);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
  } catch (e) { return null; }
}

// A zman token → minutes from local midnight, honouring its ±offset and its
// optional "<HH:MM" cap. Null when it can't be resolved (no coordinates, or
// polar), so the caller falls back to ignoring the range.
export function zmanMinutes(token, { lat, lng, tz, dateISO }) {
  const z = splitZmanToken(token);
  if (!z || typeof lat !== 'number' || typeof lng !== 'number' || !dateISO) return null;
  const [y, mo, d] = dateISO.split('-').map(Number);
  const at = (alt, rising) => {
    const ms = _sunEventMs(y, mo, d, lat, lng, alt, rising);
    return ms == null ? null : _msToMinutesInTz(ms, tz);
  };
  let base = null;
  if (z.base === 'sundown') base = at(-0.833, false);
  // Netz. Shacharit is very often set relative to it, and the Afula register
  // stores exactly that — "10 minutes before sunrise, rounded to 10".
  else if (z.base === 'sunrise') base = at(-0.833, true);
  else if (z.base === 'candle_lighting') { const s = at(-0.833, false); base = s == null ? null : s - 18; }
  else if (z.base === 'shabbat_end') base = at(-8.5, false);
  // Tzeit (צאת הכוכבים) e la fine di Shabbat sono lo stesso momento del cielo,
  // e qui si calcolano allo stesso modo: tre stelle, −8,5°. Due nomi perche'
  // chi scrive «maariv a tzeis» non sta parlando di Shabbat.
  else if (z.base === 'tzeit') base = at(-8.5, false);
  // Alot hashachar a 16,1° sotto l'orizzonte — la shita che usa anche Hebcal.
  // Serve come bordo di una finestra («maariv fino a poco prima dell'alba»),
  // dove uno scarto di qualche minuto fra le shitot non cambia niente.
  else if (z.base === 'alot') base = at(-16.1, true);
  else if (z.base === 'plag_hamincha') {
    const sr = at(-0.833, true); const ss = at(-0.833, false);
    base = (sr == null || ss == null) ? null : ss - ((ss - sr) / 12) * 1.25;
  }
  // Chatzot, mezzogiorno vero: il punto di mezzo fra netz e shkia, non le 12
  // dell'orologio. Da li' discendono gli altri due.
  else if (z.base === 'chatzot' || z.base === 'mincha_gedola' || z.base === 'chatzot_night') {
    const sr = at(-0.833, true); const ss = at(-0.833, false);
    if (sr == null || ss == null) base = null;
    else {
      const mezzo = (sr + ss) / 2;
      // Mincha gedola: mezz'ora ZEMANIT dopo chatzot — un dodicesimo del giorno
      // diviso due, non trenta minuti d'orologio (a dicembre a Londra sono 22).
      if (z.base === 'mincha_gedola') base = mezzo + ((ss - sr) / 12) / 2;
      // Chatzot halayla: dodici ore dopo mezzogiorno vero, quindi poco dopo la
      // mezzanotte dell'orologio. Riportato dentro il quadrante: chi legge la
      // finestra sa gia' che una fine prima dell'inizio e' il giorno dopo.
      else if (z.base === 'chatzot_night') base = (mezzo + 720) % 1440;
      else base = mezzo;
    }
  }
  if (base == null) return null;
  let m = Math.round(base + (z.offset || 0));
  if (z.cap != null) m = Math.min(m, z.cap);
  return m;
}
const _dayMatches = (days, code) => Array.isArray(days) && (days.includes('daily') || days.includes(code) || (days.includes('weekday') && ['mon', 'tue', 'wed', 'thu', 'fri'].includes(code)));

// --- MINYANIM CONTINUI -----------------------------------------------------
//
// Menachem, 5/11/2026: «Ci sono sinagoghe aperte tutto il tempo dove c'e' una
// preghiera ogni dieci, ogni quindici minuti. Deve apparire fra i minyanim,
// cosi' chi cerca quello piu' vicino vede che li' i minyanim sono continui».
//
// A 770 la mincha non e' alle 14:30: e' ogni quarto d'ora da mincha gedola a
// shkia. Un orario solo sarebbe una bugia — e infatti il parser ne aveva
// inventato uno, «mincha alla shkia», che mandava la gente a perderla. Ma
// senza orario la scheda spariva dall'elenco dei minyanim, che e' peggio: il
// posto dove un minyan lo trovi SEMPRE era l'unico a non comparire.
//
// Quindi una voce puo' portare, al posto di `times`, una FINESTRA:
//
//   { days: ['sun','mon','tue','wed','thu'],
//     continuous: { every: 15, from: 'mincha_gedola', until: 'sundown' } }
//
// `from` e `until` sono un token zman (sunrise, chatzot, mincha_gedola,
// sundown, tzeit, chatzot_night, con l'eventuale ±minuti) oppure un orario
// "HH:MM". Niente `times`: non c'e' un'ora da dire, c'e' un intervallo.
// IL PASSO E' FACOLTATIVO. 770 scrive «ogni 15 minuti»; gli Shtiblach di
// Zichron Moshe no — li' i minyanim si susseguono e nessuno ha mai contato i
// minuti. Pretendere un numero avrebbe voluto dire o inventarlo o lasciare
// fuori dall'elenco proprio il posto dove un minyan c'e' sempre. La finestra
// invece serve: senza «da quando a fino a quando» non c'e' niente da dire.
export const continuousEntry = (entry) => {
  const c = entry && entry.continuous;
  if (!c || typeof c !== 'object') return null;
  if (!c.from || !c.until) return null;
  const n = parseInt(c.every, 10);
  const every = (Number.isFinite(n) && n >= 1 && n <= 180) ? n : null;
  return { every, from: String(c.from), until: String(c.until) };
};

// Un bordo della finestra → minuti da mezzanotte, oppure null se non si puo'
// calcolare (manca la posizione, o e' un testo che non sappiamo leggere).
const _bordoFinestra = (v, ctx) => {
  const h = _hhmm(v);
  if (h != null) return h;
  return zmanMinutes(v, ctx);
};
// ...e la sua etichetta, per quando l'ora non si puo' calcolare: meglio
// «Mincha Gedola → Sundown» che una riga vuota.
export const windowEdgeLabel = (v) => {
  const z = splitZmanToken(v);
  if (z) return zmanTokenLabel(v);
  const h = _hhmm(v);
  if (h == null) return String(v || '');
  const hh = Math.floor(h / 60); const mm = String(h % 60).padStart(2, '0');
  const p = hh >= 12 ? 'PM' : 'AM';
  return `${hh === 0 ? 12 : hh > 12 ? hh - 12 : hh}:${mm} ${p}`;
};

// La finestra risolta per un giorno: inizio, fine e durata in minuti.
// `end` puo' cadere PRIMA di `start` sul quadrante — maariv da tzeit a chatzot
// halayla scavalca la mezzanotte — e allora la durata e' quella che scavalca.
export function continuousRun(entry, ctx) {
  const c = continuousEntry(entry);
  if (!c) return null;
  const start = _bordoFinestra(c.from, ctx);
  const end = _bordoFinestra(c.until, ctx);
  const length = (start != null && end != null)
    ? ((end - start + 1440) % 1440) || 1440
    : null;
  return {
    ...c, start, end, length,
    startLabel: windowEdgeLabel(c.from),
    endLabel: windowEdgeLabel(c.until),
  };
}

// --- "Prossimo minyan" -----------------------------------------------------
// Nella lista un negozio dice se è aperto o chiuso; una sinagoga non diceva
// niente, anche quando la sua pagina ha tutti gli orari delle tefillot
// (Menachem, 19/10/2026: «se una sinagoga ha gli orari nella pagina, come lo
// possiamo far vedere nella lista?»).
// GLI ORARI DI PREGHIERA SI POSSONO NASCONDERE SU UNA SCHEDA SOLA.
//
// Menachem, 23/10/2026, sulla scheda di Edgware Adath Yisroel: «hide
// prayer times». Non c'era nessun interruttore, e le strade erano due:
// cancellare gli orari, oppure metterci un segno.
//
// Si mette un segno, `hidden: true` dentro lo stesso `prayer_times`.
// Cancellarli vorrebbe dire buttare un dato raccolto e verificato, che fra
// sei mesi nessuno saprebbe piu' ricostruire - il `raw_text` originale
// compreso - e renderebbe la decisione irreversibile. Cosi' invece si
// toglie e si rimette cambiando una riga, e la stessa cosa vale per
// qualunque altra scheda a cui servisse.
//
// Va controllata in SEI posti, non solo sulla scheda: la pagina dei
// minyanim, il «prossimo minyan», il pianificatore di viaggio, l'HTML
// servito ai crawler e il riquadro degli orari di apertura - che si
// nasconde da solo quando ci sono quelli di preghiera, e senza questo
// controllo lascerebbe la scheda senza nessun orario.
export const oraripreghieraNascosti = (est) => !!(est && est.prayer_times && est.prayer_times.hidden);

// Qui si tira fuori il PROSSIMO minyan dall'oggetto prayer_times
// ({shacharit|mincha|arvit|kabbalat_shabbat: [{days:[…], times:[…]}]}),
// nell'ora della sinagoga, non in quella di chi guarda. Gli orari legati al
// sole ("sundown", "plag_hamincha-20") si risolvono come già fa openStatus.
// Se nessun orario è calcolabile si torna { solo: true }: la lista dirà che
// gli orari ci sono, senza inventarne uno.
const _SERVIZI = [['shacharit', 'Shacharit'], ['mincha', 'Mincha'], ['arvit', 'Maariv'], ['kabbalat_shabbat', 'Kabbalat Shabbat']];
const _GIORNI = { daily: [0, 1, 2, 3, 4, 5, 6], weekday: [0, 1, 2, 3, 4], weekdays: [0, 1, 2, 3, 4], shabbat: [6], shabbos: [6], sat: [6], sun: [0], mon: [1], tue: [2], wed: [3], thu: [4], fri: [5] };

export function nextMinyan(est) {
  if (oraripreghieraNascosti(est)) return null;
  const pt = est && est.prayer_times;
  if (!pt || typeof pt !== 'object' || Array.isArray(pt)) return null;
  const coords = est.coordinates;
  const lat = coords && typeof coords.y === 'number' ? coords.y : null;
  const lng = coords && typeof coords.x === 'number' ? coords.x : null;
  const tz = guessTimezone(est.country, lat, lng);
  const now = nowPartsInTz(tz);
  const dateISO = (() => { try { return new Date(serverNowMs()).toLocaleDateString('en-CA', { timeZone: tz }); } catch (e) { return null; } })();
  const adesso = now.day * 1440 + now.minutes;
  let migliore = null;
  let qualcosa = false;
  for (const [chiave, etichetta] of _SERVIZI) {
    for (const voce of Array.isArray(pt[chiave]) ? pt[chiave] : []) {
      const giorni = [...new Set((voce.days || []).flatMap((d) => _GIORNI[String(d).toLowerCase()] || []))];
      if (!giorni.length) continue;
      // UNA FINESTRA, NON UN'ORA. Se adesso siamo dentro, il prossimo minyan
      // e' fra zero minuti: la pastiglia dira' «minyan ora», che e' la cosa
      // piu' utile che una lista possa dire a chi cerca un minyan.
      const corsa = continuousRun(voce, { lat, lng, tz, dateISO });
      if (corsa) {
        qualcosa = true;
        if (corsa.start != null && corsa.length != null) {
          for (const g of giorni) {
            // Tre candidati: la finestra di questa settimana, quella di prima e
            // quella dopo. Serve perche' una finestra puo' scavalcare la
            // mezzanotte — e la mezzanotte fra sabato e domenica scavalca anche
            // la settimana. Si tiene la piu' vicina che non sia gia' finita.
            const base = (g * 1440 + corsa.start) - adesso;
            let quando = null;
            for (const c of [base - 7 * 1440, base, base + 7 * 1440]) {
              if (c <= -corsa.length) continue; // finita
              if (quando == null || c < quando) quando = c;
            }
            if (quando == null) continue;
            const inCorso = quando <= 0;
            const attesa = inCorso ? 0 : quando;
            if (!migliore || attesa < migliore.fra) {
              migliore = {
                fra: attesa, etichetta, giorno: g, inCorso, continuo: corsa,
                ora: `${String(Math.floor(corsa.start / 60)).padStart(2, '0')}:${String(corsa.start % 60).padStart(2, '0')}`,
              };
            }
          }
        }
        continue; // una voce continua non ha `times` da scorrere
      }
      for (const t of voce.times || []) {
        qualcosa = true;
        const min = _hhmm(t) != null ? _hhmm(t) : zmanMinutes(t, { lat, lng, tz, dateISO });
        if (min == null) continue;
        for (const g of giorni) {
          let quando = g * 1440 + min - adesso;
          if (quando < 0) quando += 7 * 1440;
          if (!migliore || quando < migliore.fra) {
            migliore = { fra: quando, etichetta, ora: `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`, giorno: g };
          }
        }
      }
    }
  }
  if (!migliore) return qualcosa ? { solo: true } : null;
  const oggi = migliore.giorno === now.day && migliore.fra < 1440;
  return {
    label: migliore.etichetta,
    time: migliore.ora,
    minutesAway: migliore.fra,
    today: oggi,
    dayCode: _DAY_CODES[migliore.giorno],
    // Solo per le finestre continue: ogni quanto (se lo sappiamo), e se e'
    // aperta adesso.
    ...(migliore.continuo
      ? { continuous: true, every: migliore.continuo.every, running: !!migliore.inCorso }
      : {}),
  };
}

export function openStatus(est) {
  const oh = est && est.opening_hours;
  if (!oh || !Array.isArray(oh.schedule) || oh.schedule.length === 0) return null;
  // Any real open/close range in the data? (A schedule that only carries notes /
  // "closed" markers can't drive an open-now check.)
  const coords = est.coordinates;
  const lat = coords && typeof coords.y === 'number' ? coords.y : null;
  const lng = coords && typeof coords.x === 'number' ? coords.x : null;
  const tz = guessTimezone(est.country, lat, lng);
  const _dateISO = (() => { try { return new Date().toLocaleDateString('en-CA', { timeZone: tz }); } catch (e) { return null; } })();
  // A range bound is either a clock time or a zman token ("shabbat_end").
  // Resolving both here is what stops a motzei-Shabbat opening being dropped.
  const _bound = (v) => { const h = _hhmm(v); return h != null ? h : zmanMinutes(v, { lat, lng, tz, dateISO: _dateISO }); };
  const hasRanges = oh.schedule.some((e) => Array.isArray(e.ranges) && e.ranges.some((r) => _bound(r.open) != null && _bound(r.close) != null));
  if (!hasRanges) return null;

  // A dated SEASON WINDOW — `season: { from, until }` as YYYY-MM-DD.
  //
  // Distinct from the per-entry `months` arrays, which describe a pattern that
  // recurs every year ("summer hours"). A season window is a specific run with
  // real dates: Capri says "open until 6 September 2026". Expressed as months
  // that would wrongly reopen the place every September, so it needs its own
  // field. Outside the window nothing is open, whatever the weekly schedule
  // says, and `season` comes back so the UI can say WHY it's shut and when it
  // returns — "Closed for the season" reads very differently from a plain
  // "Closed" to someone deciding whether to walk over.
  const seasonSpec = oh.season && typeof oh.season === 'object' ? oh.season : null;
  if (seasonSpec) {
    const todayISO = new Date().toLocaleDateString('en-CA', { timeZone: tz }); // YYYY-MM-DD there
    const before = seasonSpec.from && todayISO < seasonSpec.from;
    const after = seasonSpec.until && todayISO > seasonSpec.until;
    if (before || after) {
      return {
        open: false,
        state: 'closed',
        closesAt: null,
        opensNext: null,
        closingSoon: false,
        seasonClosed: true,
        // Only promise a reopening when a real date is on file; otherwise the
        // UI says "Closed for the season" and stops there rather than inventing
        // a return date we don't know.
        reopensOn: before ? seasonSpec.from : (seasonSpec.next_from || null),
        seasonNote: seasonSpec.note || null,
      };
    }
  }

  const { day, minutes, month } = nowPartsInTz(tz);
  const today = _DAY_CODES[day];
  const yest = _DAY_CODES[(day + 6) % 7];
  const inMonth = (e) => !Array.isArray(e.months) || e.months.includes(month); // seasonal entries only count in their months

  // A named day BEATS `daily`.
  //
  // Six kosher places said `daily 11:00-22:00` alongside `sat: closed` and were
  // shown OPEN on Shabbat, because the loop below skipped closed entries
  // instead of letting them override — `if (e.closed) continue` drops the very
  // entry that was meant to say "not today". Naming a day is the more specific
  // statement, so when any entry names today, only those entries count and the
  // catch-all `daily` steps aside. That makes `sat: closed` do what it plainly
  // means, and equally lets `sat 20:00-23:00` override `daily: closed`.
  const _named = (e) => Array.isArray(e.days) && e.days.some((d) => d !== 'daily' && d !== 'weekday');
  const _forDay = (code) => {
    const live = oh.schedule.filter((e) => inMonth(e) && _dayMatches(e.days, code));
    const specific = live.filter(_named);
    return specific.length ? specific : live;
  };
  const _todays = _forDay(today);
  const _yests = _forDay(yest);

  let open = false, closesAt = null;
  for (const e of _todays) {
    if (e.closed || !Array.isArray(e.ranges)) continue;
    if (_dayMatches(e.days, today)) {
      for (const r of e.ranges) {
        const o = _bound(r.open), c = _bound(r.close); if (o == null || c == null) continue;
        if (c > o) { if (minutes >= o && minutes < c) { open = true; closesAt = Math.min(closesAt ?? Infinity, c); } }
        else if (minutes >= o) { open = true; closesAt = Math.min(closesAt ?? Infinity, c + 1440); } // overnight, started today
      }
    }
  }
  for (const e of _yests) { // yesterday's overnight range spilling into this morning
    if (e.closed || !Array.isArray(e.ranges)) continue;
    for (const r of e.ranges) {
      const o = _bound(r.open), c = _bound(r.close); if (o == null || c == null) continue;
      if (c <= o && minutes < c) { open = true; closesAt = Math.min(closesAt ?? Infinity, c); }
    }
  }

  // Next opening within the coming week (for the "Closed · opens …" hint).
  let opensNext = null;
  if (!open) {
    for (let d = 0; d < 8 && !opensNext; d++) {
      const code = _DAY_CODES[(day + d) % 7];
      let best = null;
      for (const e of oh.schedule) {
        if (e.closed || !Array.isArray(e.ranges) || !_dayMatches(e.days, code) || !inMonth(e)) continue;
        for (const r of e.ranges) {
          const o = _bound(r.open); if (o == null) continue;
          if (d === 0 && o <= minutes) continue; // already passed earlier today
          best = best == null ? o : Math.min(best, o);
        }
      }
      if (best != null) opensNext = { dayOffset: d, dayCode: code, minutes: best };
    }
  }

  const remaining = open && closesAt != null ? closesAt - minutes : null;
  return {
    open,
    state: open ? 'open' : 'closed',
    closesAt: closesAt === Infinity ? null : closesAt, // minutes-from-midnight (may exceed 1440 for overnight)
    opensNext,                                         // { dayOffset, dayCode, minutes } | null
    closingSoon: open && remaining != null && remaining > 0 && remaining <= 60,
  };
}

// Country → international calling code (E.164)
const COUNTRY_PHONE_CODES = {
  // Europe
  Albania: '+355', Andorra: '+376', Austria: '+43', Belarus: '+375', Belgium: '+32',
  'Bosnia and Herzegovina': '+387', Bulgaria: '+359', Croatia: '+385', Cyprus: '+357',
  'Czech Republic': '+420', Czechia: '+420', Denmark: '+45', Estonia: '+372',
  Finland: '+358', France: '+33', Germany: '+49', Greece: '+30', Hungary: '+36',
  Iceland: '+354', Ireland: '+353', Italy: '+39', Kosovo: '+383', Latvia: '+371',
  Liechtenstein: '+423', Lithuania: '+370', Luxembourg: '+352', Malta: '+356',
  Moldova: '+373', Monaco: '+377', Montenegro: '+382', Nepal: '+977', Netherlands: '+31',
  Netherland: '+31', Holland: '+31',  // common variant spellings of NL
  'North Macedonia': '+389', Norway: '+47', Poland: '+48', Portugal: '+351',
  Romania: '+40', Russia: '+7', 'San Marino': '+378', Serbia: '+381',
  Slovakia: '+421', Slovenia: '+386', Spain: '+34', Sweden: '+46',
  Switzerland: '+41', Turkey: '+90', Ukraine: '+380', 'United Kingdom': '+44',
  UK: '+44', 'Vatican City': '+39',
  // Common others
  'United States': '+1', USA: '+1', Canada: '+1', Israel: '+972', Australia: '+61',
  'United Arab Emirates': '+971', UAE: '+971',
};

// Country → ISO 3166-1 alpha-2 code (lowercase, for image URLs)
export const COUNTRY_CODES = {
  Albania: 'al', Andorra: 'ad', Austria: 'at', Belarus: 'by', Belgium: 'be',
  'Bosnia and Herzegovina': 'ba', Bulgaria: 'bg', Croatia: 'hr', Cyprus: 'cy',
  'Czech Republic': 'cz', Czechia: 'cz', Denmark: 'dk', Estonia: 'ee',
  Finland: 'fi', France: 'fr', Germany: 'de', Greece: 'gr', Hungary: 'hu',
  Iceland: 'is', Ireland: 'ie', Italy: 'it', Kosovo: 'xk', Latvia: 'lv',
  Liechtenstein: 'li', Lithuania: 'lt', Luxembourg: 'lu', Malta: 'mt',
  Moldova: 'md', Monaco: 'mc', Montenegro: 'me', Nepal: 'np', Netherlands: 'nl',
  Netherland: 'nl', Holland: 'nl',  // common variant spellings of NL
  'North Macedonia': 'mk', Norway: 'no', Poland: 'pl', Portugal: 'pt',
  Romania: 'ro', Russia: 'ru', 'San Marino': 'sm', Serbia: 'rs',
  Slovakia: 'sk', Slovenia: 'si', Spain: 'es', Sweden: 'se',
  Switzerland: 'ch', Turkey: 'tr', Ukraine: 'ua', 'United Kingdom': 'gb',
  UK: 'gb', 'Vatican City': 'va',
  'United States': 'us', USA: 'us', Canada: 'ca', Israel: 'il', Australia: 'au',
  'United Arab Emirates': 'ae', UAE: 'ae', Thailand: 'th',
  Mexico: 'mx', Argentina: 'ar', Brazil: 'br', 'South Africa': 'za', Botswana: 'bw',
  Japan: 'jp', China: 'cn', India: 'in', Georgia: 'ge', Azerbaijan: 'az',
};

// Seasonal prayer times: is the given location currently in its daylight-saving
// ("summer") period? Uses the EU/US-style clock-change window (last Sun of
// Distance shown in the units of the PLACE's country, not the reader's browser.
// Someone planning a walk to a hotel in Manhattan thinks in feet and miles; the
// same walk in Paris is metres. Keying off the listing's country means a French
// visitor browsing New York still gets the units the street signs use there.
//
// Only three countries are properly imperial (US, Liberia, Myanmar). The UK is a
// genuine hybrid and gets its own mode: metres for a walkable distance, miles for
// anything longer, which is what British signage and everyday speech do.
const IMPERIAL_COUNTRIES = new Set(['united states', 'usa', 'us', 'liberia', 'myanmar', 'burma']);
const HYBRID_COUNTRIES = new Set(['united kingdom', 'uk', 'great britain', 'england', 'scotland', 'wales', 'northern ireland']);

export const distanceUnits = (country) => {
  const c = String(country || '').trim().toLowerCase();
  if (IMPERIAL_COUNTRIES.has(c)) return 'imperial';
  if (HYBRID_COUNTRIES.has(c)) return 'hybrid';
  return 'metric';
};

// Compare place names ignoring case AND accents. The backend folds diacritics
// when it filters by state, so the UI has to fold the same way — otherwise a
// link that says ?state=Ile-de-France returns the right rows but leaves the
// dropdown showing "All States/Regions".
// Same algorithm as buildAccentTable/foldName in the backend's
// routes/categories.js — both derive the mapping from Unicode decomposition
// rather than a hand-typed list, so they can't drift apart. Ø and the Romanian
// comma-below letters don't decompose and are mapped by hand on both sides.
// scripts/check-fold.js in the backend verifies the two agree.
const ACCENT_MAP = (() => {
  const m = new Map([['Ø', 'O'], ['ø', 'o'], ['Ș', 'S'], ['ș', 's'], ['Ț', 'T'], ['ț', 't']]);
  for (let cp = 0x00c0; cp <= 0x017f; cp++) {
    const ch = String.fromCodePoint(cp);
    const base = ch.normalize('NFD').replace(/\p{Diacritic}/gu, '');
    if (base.length === 1 && base !== ch && /[A-Za-z]/.test(base)) m.set(ch, base);
  }
  return m;
})();
export const foldName = (s) => [...String(s || '')].map((c) => ACCENT_MAP.get(c) || c).join('').toLowerCase().trim();

// km (a number) → a short display string. Returns '' for anything non-numeric so
// callers can render it unconditionally.
// QUALE UNITA' USA CHI LEGGE, non il posto che sta leggendo.
//
// Menachem, 27/10/2026: «mostra distanze, se metri o feet, a seconda
// della lingua o della provenienza».
//
// Prima si mostravano tutte e due — «787 m (0.5 mi)» — col
// ragionamento che un americano non ha il senso di 800 metri. Il
// ragionamento regge, il risultato no: due unita' in una riga stretta
// si mangiano il posto del NOME dell'albergo, che e' la cosa per cui
// uno quella riga la legge. Meglio una, giusta.
//
// Si guarda prima la lingua scelta nel sito: chi legge in ebraico, in
// francese o in italiano i piedi non li usa, qualunque browser abbia.
// In inglese decide la regione del browser (en-US, en-GB, en-AU). Se
// la regione non si sa, inglese vuol dire imperiale: il grosso di chi
// ci legge in inglese sta negli Stati Uniti.
export function unitaLettore() {
  const lingua = (() => { try { return getLang(); } catch (e) { return 'en'; } })();
  if (lingua && lingua !== 'en') return 'metric';
  let regione = '';
  try {
    const tag = (navigator.languages && navigator.languages[0]) || navigator.language || '';
    regione = (String(tag).split('-')[1] || '').toUpperCase();
  } catch (e) { regione = ''; }
  if (['US', 'LR', 'MM'].includes(regione)) return 'imperial';
  if (regione === 'GB') return 'hybrid';
  if (regione) return 'metric';
  return 'imperial';
}

// `country` resta nella firma — la chiamano sette posti — ma non
// decide piu' l'unita': decide chi legge. Serve ancora a chi volesse
// sapere dov'e' il posto senza rifare il conto.
export function formatDistance(km, country) {
  if (typeof km !== 'number' || !Number.isFinite(km)) return '';
  const mode = unitaLettore();

  // Quale unita' imperiale dipende dalla distanza. I piedi sono il
  // compagno naturale di una camminata breve (250 m ≈ 820 ft) ma
  // assurdi oltre: «800 m (2620 ft)» non dice niente a nessuno.
  const feet = km * 3280.84;
  const miles = km * 0.621371;
  const asMetric = km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(km < 10 ? 1 : 0)} km`;
  // Feet to the nearest 10 — "820 ft", not "817 ft", which would imply a
  // precision the coordinates don't have.
  const asImperial = km < 0.3 ? `${Math.round(feet / 10) * 10} ft` : `${miles.toFixed(miles < 10 ? 1 : 0)} mi`;

  // Una sola, quella di chi legge. Il Regno Unito e' «ibrido» perche'
  // li' si dicono i metri per una camminata e le miglia per una
  // strada, ed e' comunque una unita' alla volta.
  if (mode === 'imperial') return asImperial;
  if (mode === 'hybrid') return km < 1 ? asMetric : asImperial;
  return asMetric;
}

// March → last Sun of October) for the Northern hemisphere, inverted for the
// Southern — a close, dependency-free proxy for DST that covers virtually all
// communities. lat null/unknown → treat as Northern.
export const isSummerDST = (lat, date = new Date()) => {
  const y = date.getUTCFullYear();
  const lastSundayUTC = (month) => { // month 0-11
    const d = new Date(Date.UTC(y, month + 1, 0)); // last day of that month
    d.setUTCDate(d.getUTCDate() - d.getUTCDay());   // back up to Sunday
    return d.getTime();
  };
  const t = date.getTime();
  const northSummer = t >= lastSundayUTC(2) && t < lastSundayUTC(9);
  return (lat == null || lat >= 0) ? northSummer : !northSummer;
};
export const currentSeason = (lat, date) => (isSummerDST(lat, date) ? 'summer' : 'winter');
// A prayer-times entry shows if it has no season, or matches the current one.
export const entryInSeason = (entry, lat, date) =>
  !entry || !entry.season || entry.season === currentSeason(lat, date);

// ── Holiday times ──────────────────────────────────────────────────────────
// The weekly buckets above repeat forever, so a one-off like the Hoshana Rabba
// shacharit can't go in them: filed under `holiday` it would claim to be the
// time for every chag, and filed under a weekday it would still be there next
// Friday. prayer_times.holiday_times entries instead carry real calendar dates
// and are shown only in the run-up, then disappear on their own.
//
//   { dates: ['2026-10-02'], occasion: 'Hoshana Rabba',
//     service: 'Shacharit', times: ['06:30'], note: '1st tefillah' }
export const HOLIDAY_LEAD_DAYS = 21;

// Today where the PLACE is, as YYYY-MM-DD — a Rome minyan at 6:30 is a Rome
// date, whoever is reading.
export const localDateISO = (tz, now) => {
  const d = now || new Date();
  try {
    // en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d);
  } catch (_) {
    return new Date(d).toISOString().slice(0, 10);
  }
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const daysApart = (from, to) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);

// The next date this entry still applies to, or null once they have all passed.
export const holidayEntryDate = (entry, tz, now) => {
  const dates = (Array.isArray(entry && entry.dates) ? entry.dates : [])
    .filter((d) => ISO_DATE.test(String(d))).sort();
  if (!dates.length) return null;
  const today = localDateISO(tz, now);
  const upcoming = dates.find((d) => d >= today && daysApart(today, d) <= HOLIDAY_LEAD_DAYS);
  return upcoming || null;
};
export const holidayEntryLive = (entry, tz, now) => holidayEntryDate(entry, tz, now) !== null;

// Sun-based time tokens carry an optional minute offset — "sundown-40" =
// 40 min before sunset, "candle_lighting+15" = 15 min after. Split into
// { base, offset } (offset in minutes, signed) or null if not a zman token.
export const splitZmanToken = (t) => {
  // base[±offset][<HH:MM] — the optional "<HH:MM" is a "not later than" cap: the
  // effective time is min(zman, cap). e.g. "sundown<19:00".
  // `chatzot_night` PRIMA di `chatzot`, e `mincha_gedola` prima di tutto il
  // resto: l'alternanza prende la prima che combacia.
  const m = /^(candle_lighting|sundown|plag_hamincha|shabbat_end|sunrise|mincha_gedola|chatzot_night|chatzot|tzeit|alot)([+-]\d+)?(?:<(\d{1,2}):(\d{2}))?$/.exec(String(t || ''));
  if (!m) return null;
  const cap = m[3] != null ? parseInt(m[3], 10) * 60 + parseInt(m[4], 10) : null;
  return { base: m[1], offset: m[2] ? parseInt(m[2], 10) : 0, cap };
};
export const ZMAN_LABEL = {
  candle_lighting: 'Candle lighting', sundown: 'Sundown', plag_hamincha: 'Plag Hamincha',
  shabbat_end: 'End of Shabbat', sunrise: 'Sunrise',
  // I bordi di una finestra di minyanim continui (vedi `continuousRun`).
  chatzot: 'Midday', mincha_gedola: 'Mincha Gedola', tzeit: 'Nightfall',
  chatzot_night: 'Halachic midnight', alot: 'Dawn',
};
// Human label for a zman token, e.g. "Sundown −40 min".
export const zmanTokenLabel = (t) => {
  const z = splitZmanToken(t);
  if (!z) return null;
  if (!z.offset) return ZMAN_LABEL[z.base];
  return `${ZMAN_LABEL[z.base]} ${z.offset > 0 ? '+' : '−'}${Math.abs(z.offset)} min`;
};

// City → URL slug, mirroring the backend's basicSlugify (NFKD accent-strip,
// lowercase, non-alphanumeric → dash). Used to build /guide/:cc/:city links.
export const citySlugify = (s) =>
  String(s || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/--+/g, '-')
    .slice(0, 80);

// Resolve a country name to its ISO-2 code. Tolerates case + leading/trailing
// whitespace ('italy' → 'it') so messy DB data still gets a flag.
export const countryCode = (country) => {
  if (!country) return null;
  if (COUNTRY_CODES[country]) return COUNTRY_CODES[country];
  // Fallback: case-insensitive lookup against the map keys
  const norm = String(country).trim();
  if (!norm) return null;
  const lc = norm.toLowerCase();
  for (const key of Object.keys(COUNTRY_CODES)) {
    if (key.toLowerCase() === lc) return COUNTRY_CODES[key];
  }
  return null;
};

// Hebcal's zmanim API now REQUIRES a timezone. Resolve a place's IANA tzid from
// its country — exact for single-timezone countries (nearly all our coverage)
// — with a longitude split for the few multi-timezone ones, and a fixed-offset
// fallback for anything unknown.
const TZ_BY_COUNTRY = {
  israel: 'Asia/Jerusalem', italy: 'Europe/Rome', france: 'Europe/Paris',
  'united kingdom': 'Europe/London', uk: 'Europe/London', germany: 'Europe/Berlin',
  hungary: 'Europe/Budapest', cyprus: 'Asia/Nicosia', spain: 'Europe/Madrid',
  netherlands: 'Europe/Amsterdam', netherland: 'Europe/Amsterdam', holland: 'Europe/Amsterdam',
  belgium: 'Europe/Brussels', switzerland: 'Europe/Zurich', austria: 'Europe/Vienna',
  greece: 'Europe/Athens', poland: 'Europe/Warsaw', ukraine: 'Europe/Kyiv',
  portugal: 'Europe/Lisbon', turkey: 'Europe/Istanbul', czechia: 'Europe/Prague',
  'czech republic': 'Europe/Prague', sweden: 'Europe/Stockholm', ireland: 'Europe/Dublin',
  romania: 'Europe/Bucharest', denmark: 'Europe/Copenhagen', croatia: 'Europe/Zagreb',
  thailand: 'Asia/Bangkok', norway: 'Europe/Oslo', bulgaria: 'Europe/Sofia',
  belarus: 'Europe/Minsk', finland: 'Europe/Helsinki', lithuania: 'Europe/Vilnius',
  latvia: 'Europe/Riga', estonia: 'Europe/Tallinn', serbia: 'Europe/Belgrade',
  monaco: 'Europe/Monaco', malta: 'Europe/Malta', iceland: 'Atlantic/Reykjavik',
  gibraltar: 'Europe/Gibraltar', luxembourg: 'Europe/Luxembourg', slovakia: 'Europe/Bratislava',
  slovenia: 'Europe/Ljubljana', moldova: 'Europe/Chisinau', montenegro: 'Europe/Podgorica',
  albania: 'Europe/Tirane', 'north macedonia': 'Europe/Skopje',
  'bosnia and herzegovina': 'Europe/Sarajevo', kosovo: 'Europe/Belgrade', 'san marino': 'Europe/Rome',
  andorra: 'Europe/Andorra', liechtenstein: 'Europe/Vaduz', 'vatican city': 'Europe/Rome',
  'united arab emirates': 'Asia/Dubai', uae: 'Asia/Dubai',
};
export function guessTimezone(country, lat, lng) {
  const c = String(country || '').trim().toLowerCase();
  if (TZ_BY_COUNTRY[c]) return TZ_BY_COUNTRY[c];
  const L = typeof lng === 'number' ? lng : 0;
  if (['united states', 'usa', 'us'].includes(c)) return L < -115 ? 'America/Los_Angeles' : L < -100 ? 'America/Denver' : L < -87 ? 'America/Chicago' : 'America/New_York';
  if (c === 'canada') return L < -120 ? 'America/Vancouver' : L < -100 ? 'America/Edmonton' : L < -90 ? 'America/Winnipeg' : L < -60 ? 'America/Toronto' : 'America/Halifax';
  if (c === 'australia') return L < 129 ? 'Australia/Perth' : L < 141 ? 'Australia/Adelaide' : 'Australia/Sydney';
  if (c === 'russia') return L < 40 ? 'Europe/Moscow' : L < 60 ? 'Europe/Samara' : L < 83 ? 'Asia/Yekaterinburg' : L < 98 ? 'Asia/Novosibirsk' : 'Asia/Krasnoyarsk';
  // Last resort: fixed offset from longitude (Etc/GMT sign is inverted).
  if (typeof lng === 'number') { const off = Math.round(lng / 15); return `Etc/GMT${off <= 0 ? '+' : '-'}${Math.abs(off)}`; }
  return 'UTC';
}

// Resolve the address-line for a picked geocoder suggestion. The geocoder
// occasionally returns only a bare house number with no street (common for US
// street-centerline hits); in that case fold in the street from the display
// name's first segment, e.g. "4020" + "Northwest 101st Drive, Coral Springs…"
// → "4020 Northwest 101st Drive". A safety net independent of the backend.
export const resolvePickedAddress = (partsAddress, displayName) => {
  const seg = displayName ? String(displayName).split(',')[0].trim() : '';
  let addr = (partsAddress || '').trim();
  if (/^\d+[a-z]?$/i.test(addr)) {            // addr is just a bare house number
    if (seg && /^\d/.test(seg)) addr = seg;   // the display segment already has the number
    else if (seg) addr = `${addr} ${seg}`;    // combine "4020" + "Northwest 101st Drive"
  }
  return addr || seg;
};

// Primary subtype values per category
export const SUBTYPE_OPTIONS = {
  // Yemenite (Teimani) is its own nusach, not a flavour of Sephardi —
  // Tel Aviv alone has 52 Yemenite synagogues.
  synagogue_shul: ['Ashkenaz', 'Sefarad', 'Sephardi', 'Ari', 'Italian', 'Yemenite'],
  // Mikvah subcategory = who/what it serves. A construction/halachic detail such
  // as "Bor al gabei bor" is NOT a subcategory — it goes in the kosher-supervision
  // field (supervision_level) via the Kosher-detail editor.
  mikvah_ritual_bath: ['Women', 'Men', 'Utensils'],
  kosher_restaurant: ['Meat', 'Dairy', 'Parve'],
  kosher_eatery: ['Meat', 'Dairy', 'Parve'],
  kosher_catering: ['Meat', 'Dairy', 'Parve'],
  kosher_bakery: ['Meat', 'Dairy', 'Parve'],
  // "Grocery" = a small kosher grocery. "Kosher store" = a general kosher shop
  // (products/deliveries) that isn't specifically a supermarket/bakery/butcher.
  // "Factory" = a certified production plant (bakery products, salads, meat,
  // spices). Not somewhere to turn up and buy, but the certificate is real and
  // the Israeli council registers are full of them. The Tel Aviv import was
  // already writing this value before it was listed here, so those rows sat
  // outside every filter.
  // 'Wholesaler' is a supplier, not a shop: it sells to the trade, so the
  // listing exists to explain who stands behind the kosher goods in the
  // shops rather than to send anyone to the door. Distinct from 'Factory',
  // which makes the food — a wholesaler may do neither or both.
  kosher_store: ['Bakery', 'Butcher', 'Fishmonger', 'Supermarket', 'Grocery', 'Nuts & dried fruit', 'Kosher store', 'Kosher section', 'Wine', 'Deli', 'Pastry shop', 'Pharmacy', 'Factory', 'Wholesaler'],
  // Hotel + B&B + Hostel + Residence + Apartment are all under kosher_hotel
  // now (merged from the old 'accommodation' and 'shabbat_friendly_accommodation'
  // slugs). Subtype distinguishes which kind of stay it is.
  kosher_hotel: ['Hotel', 'B&B', 'Hostel', 'Residence', 'Apartment', 'Guesthouse'],
  accommodation: ['Hostel', 'B&B', 'Residence', 'Apartment'], // legacy — no live rows
};

// Kosher level/stringency flags — multi-select since a place can be both
// e.g. Glatt + Chalav Israel. Free-text "Other" still allowed alongside.
// Spelling is the owner's house style: "Chalav Israel" / "Pat Israel"
// (not the Yisrael/Pas variants) — the normalizer maps every variant to these.
export const KOSHER_LEVELS = [
  'Glatt',
  'Halak',             // Sephardi equivalent of Glatt — the MEAT standard (smooth
                       // lungs), distinct from Bishul (how it's cooked)
  'Lubavitch Shechita', // meat slaughtered to the Chabad-Lubavitch shechita standard
  'Mehadrin',          // covers Lemehadrin (Lemehadrin was a duplicate — same meaning)
  'Mehadrin min hamehadrin', // the Afula rabbanut's strictest tier — a level, not
                       // a separate hechsher: it issues one logo for all three.
                       // Jerusalem is the other way round, two logos, two records.
  // «Hidur hakashrut» NON sta qui, ed era il commento stesso a dirlo:
  // «Netanya's בד"ץ בהידור הכשרות and Sderot's כתר ההידור [...] both issue
  // it under their own mark». Un livello che viene rilasciato sotto un
  // proprio marchio e' un CERTIFICATORE, non un livello. Le 59 schede che
  // lo portavano sono tutte di Netanya e portano tutte «Badatz Netanya»,
  // il cui nome ebraico e' esattamente בד"ץ בהידור הכשרות: l'etichetta
  // ripeteva quello che il certificatore gia' dice. Tolta il 31/10/2026.
  'Mashgiach Temidi',  // a full-time on-site mashgiach (constant supervision, vs. yotze-venichnas / periodic visits)
  'Chalav Israel',
  'Chalav Akum',
  'Pat Israel',
  'Bishul Yisrael',
  'Bishul Beit Yosef',   // stricter Sephardi standard (a Jew lights the fire), per the Beit Yosef
  'Bishul Beit Yosef on request',  // kept to the Beit Yosef standard only when asked in advance
  'Vegetables checked',  // produce inspected for insects
  'Yashan',              // covers Kemach Yashan (Yashan flour) — same concept
  'Non-Gebrokts',       // Pesach: no matzah wetted. Same class as Non-Kitniot
                        // below — three Pesach programmes already record it, so
                        // it is a real standard that was simply missing here.
  'Shmita-compliant',
  'Shmita Lechumra',     // shmitta kept to the stricter opinion — otzar beit din
                         // or imported produce rather than relying on the heter
                         // mechira. Spelled "Shmita" to match the entry above;
                         // kitchens often write it "Shmitta Lechumra".
  // Pesach-specific stringencies
  // «Non-Gebrokts» sta gia' qui sopra, insieme a Yashan: era scritto due
  // volte, e il secondo riempiva il menu di una voce doppia.
  'Non-Kitniot',
];

// Mikvah kosher construction/halachic methods — the "kosher detail" for a
// mikvah (distinct from the Women/Men/Utensils subcategory). Multi-select.
export const MIKVAH_LEVELS = [
  'Bor al gabei bor',
  'Bor min hatzad',
  'Mei Gheshamim',
  'Hashaka',
  'Zria',
];

// Hotel affiliate links — Expedia Group runs on Partnerize; hotels.com is part
// of Expedia Group, so a Partnerize deep-link with our camref tracks bookings.
// hotelSearchUrl('Vienna, Austria') → a tracked hotels.com search for that
// destination (a city, area, or "<hotel name>, <city>" to surface one hotel).
//
// AFFILIATE_ENABLED gates every affiliate button site-wide. PARTNERIZE_CAMREF is
// the Expedia Group campaign reference from Partnerize's Link Builder — NOT the
// publisher ID '1100l429045' (prf.hn rejects that as an "Invalid Link"). The
// prf.hn deep-link below works for both hotels.com and expedia.com (flights/cars)
// with this camref. To pause affiliates, just flip AFFILIATE_ENABLED to false.
export const AFFILIATE_ENABLED = true;
export const PARTNERIZE_CAMREF = '1110lGnrK';
// Wrap any Expedia-Group target (hotels.com / expedia.com) in a tracked
// Partnerize deep-link.
export function partnerizeUrl(target) {
  return `https://prf.hn/click/camref:${PARTNERIZE_CAMREF}/destination:${encodeURIComponent(target)}`;
}
export function hotelSearchUrl(destinationText) {
  return partnerizeUrl(`https://www.hotels.com/Hotel-Search?destination=${encodeURIComponent(destinationText)}`);
}
// Hotels near a specific point — latLong alone so hotels.com centers on the pin
// and surfaces the closest properties. We deliberately DON'T pass a city
// `destination`: that overrides the coordinates with a whole-city list (which is
// exactly the "shows city hotels, not nearby" bug). `label` is unused, kept for
// call-site compatibility.
// Hotels closest to a place: give hotels.com the place's street ADDRESS as the
// destination + sort=DISTANCE and it ranks the nearest hotels first (coordinates
// or a place name don't work — a full address does). Dates default ~2 weeks out
// so prices show; the user adjusts them. Pass the fullest address you have
// ("<address>, <area>, <city>, <country>"); falls back to a city search.
export function hotelSearchNearUrl(destinationText) {
  const p = new URLSearchParams({
    rooms: '1', adults: '1', splitView: 'true',
    destination: destinationText || '', sort: 'DISTANCE',
    startDate: expediaDate(14), endDate: expediaDate(15),
  });
  return partnerizeUrl(`https://www.hotels.com/Hotel-Search?${p.toString()}`);
}
// Flights land on the reliable product page — Expedia's /go/flight search deeplink
// doesn't resolve on www.expedia.com (and a flight search needs an origin anyway).
// Cars, however, DO work via the /go/car deeplink (confirmed on the live site),
// so we pre-fill the pickup city when we have one; default dates ~3 weeks out.
function expediaDate(daysFromNow) {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toISOString().slice(0, 10); // YYYY-MM-DD
}
// Flights: Expedia's Flights-Search consumer URL (the format that actually works),
// as a roundtrip to <destinationText> with the ORIGIN left blank — Expedia opens
// with the destination filled and the traveler picks their departure. Dates
// default ~3 weeks out / +1 week return. Without a destination we link the plain
// Flights page.
export const flightsUrl = (destinationText) => {
  if (!destinationText) return partnerizeUrl('https://www.expedia.com/Flights');
  const out = new Date(); out.setDate(out.getDate() + 21);
  const back = new Date(); back.setDate(back.getDate() + 28);
  const mdy = (d) => `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  const ymd = (d) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
  const dep = mdy(out), ret = mdy(back);
  const dest = encodeURIComponent(destinationText); // e.g. "Rome, Italy" -> "Rome%2C%20Italy"
  const url = 'https://www.expedia.com/Flights-Search?flight-type=on&mode=search&trip=roundtrip'
    + `&leg1=from:,to:${dest},departure:${dep}TANYT,toType:METROCODE`
    + `&leg2=from:${dest},to:,departure:${ret}TANYT,fromType:METROCODE`
    + `&options=cabinclass:economy&fromDate=${dep}&toDate=${ret}&d1=${ymd(out)}&d2=${ymd(back)}&passengers=adults:1,infantinlap:N`;
  return partnerizeUrl(url);
};
export const carRentalUrl = (destinationText) => {
  if (!destinationText) return partnerizeUrl('https://www.expedia.com/Cars');
  const pickup = expediaDate(21), drop = expediaDate(24);
  return partnerizeUrl(`https://www.expedia.com/go/car/search/Place/${pickup}/${drop}?PickUpLoc=${encodeURIComponent(destinationText)}&Distance=10&PickUpTime=11AM&DropTime=11AM&Class=NoPreference`);
};
export const activitiesUrl = (destinationText) =>
  partnerizeUrl(`https://www.expedia.com/things-to-do/search?location=${encodeURIComponent(destinationText)}`);

// Seasonal filters — a sukkah (Sukkot) and open-on-Pesach only matter around the
// holiday, so the list-page filters auto-show in a generous Gregorian window that
// brackets the Hebrew-calendar dates (which drift year to year but stay within
// these months), plus a few weeks of planning lead time.
export function isSukkahSeason(now = new Date()) {
  const m = now.getMonth() + 1, d = now.getDate();
  return m === 9 || (m === 10 && d <= 24); // ~Sep 1 – Oct 24 (Sukkot is 15–23 Tishrei)
}
export function isPesachSeason(now = new Date()) {
  const m = now.getMonth() + 1;
  return m === 3 || m === 4; // Mar 1 – Apr 30 (Pesach is 15–22 Nisan)
}
// Flight + hotel bundles (Expedia's higher-commission product). No reliable
// destination deeplink, so we land on the packages page for the user to search.
// Vacation packages (flight + hotel) — Expedia's Hotel-Search in package mode
// (packageType=fh), to <destinationText> with the origin left blank (drops the
// internal regionId/misId tokens, which we can't generate per city; the text
// destination resolves like the hotel search does). Dates ~3 weeks out.
export const packagesUrl = (destinationText) => {
  if (!destinationText) return partnerizeUrl('https://www.expedia.com/Vacation-Packages');
  const url = 'https://www.expedia.com/Hotel-Search?packageType=fh&searchProduct=hotel&tripType=ROUND_TRIP&cabinClass=COACH&adults=2&sort=RECOMMENDED'
    + `&destination=${encodeURIComponent(destinationText)}&startDate=${expediaDate(21)}&endDate=${expediaDate(24)}`;
  return partnerizeUrl(url);
};

// Canonical category display order — by traveler need rather than raw count:
// orientation → prayer & ritual → food → lodging → kosher authority →
// shopping → sightseeing → services → community/media. Used to order the
// home-page grid, the list-page category dropdown, and the grouped results.
// Any type not listed sorts to the end (alphabetically among themselves).
export const CATEGORY_ORDER = [
  'information_point',
  'synagogue_shul',
  'mikvah_ritual_bath',
  'eruv',
  'kosher_restaurant',
  'kosher_eatery',
  'kosher_bakery',
  'kosher_store',
  'kosher_catering',
  'cooking_class',
  'shabbat_meal',
  'kosher_event_venue',
  'kosher_hotel',
  'kosher_vacation',
  'kosher_cruise',
  'kosher_travel_planner',
  'kosher_travel_agency',
  'kosher_certification_hechsher',
  'judaica_store',
  // Museums ahead of tour guides: a museum is a place you can simply turn up
  // to, a guided tour has to be arranged, so the museum is the more useful
  // first answer when someone is browsing what to see.
  'jewish_museum',
  'jewish_tour',
  'jewish_attraction',
  'jewish_cemetery',
  'jewish_school',
  'kosher_senior_living',
  'jewish_driver',
  'event_planner',
  'jewish_photographer',
  'jewish_music',
  'jewish_periodical',
];

// Parse a pasted/uploaded boundary (used by the Eruv category) into a GeoJSON
// object ready to store + draw with Leaflet. Accepts raw GeoJSON, or a KML
// export (Google My Maps / Google Earth) from which we pull every LineString
// and Polygon. Returns a GeoJSON object, or null if nothing usable was found.
export function parseBoundaryInput(text) {
  if (!text || !String(text).trim()) return null;
  const t = String(text).trim();
  // GeoJSON — anything starting with "{" that parses and has a `type`.
  if (t[0] === '{') {
    try { const g = JSON.parse(t); if (g && g.type) return g; } catch (e) { /* not JSON */ }
    return null;
  }
  // KML / XML — extract LineString + Polygon geometries (namespace-agnostic).
  if (typeof DOMParser === 'undefined') return null;
  let doc;
  try { doc = new DOMParser().parseFromString(t, 'application/xml'); } catch (e) { return null; }
  if (doc.getElementsByTagName('parsererror').length) return null;
  const parseCoords = (raw) => (raw || '').trim().split(/\s+/)
    .map((tuple) => { const [lng, lat] = tuple.split(',').map(Number); return [lng, lat]; })
    .filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]));
  const features = [];
  const polys = doc.getElementsByTagNameNS('*', 'Polygon');
  for (let i = 0; i < polys.length; i++) {
    const c = polys[i].getElementsByTagNameNS('*', 'coordinates')[0];
    const ring = c ? parseCoords(c.textContent) : [];
    if (ring.length >= 3) features.push({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } });
  }
  const lines = doc.getElementsByTagNameNS('*', 'LineString');
  for (let i = 0; i < lines.length; i++) {
    const c = lines[i].getElementsByTagNameNS('*', 'coordinates')[0];
    const pts = c ? parseCoords(c.textContent) : [];
    if (pts.length >= 2) features.push({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: pts } });
  }
  if (!features.length) return null;
  return { type: 'FeatureCollection', features };
}

// Rank for sorting; unlisted types get a large sentinel so they fall to the end.
export const categoryRank = (type) => {
  const i = CATEGORY_ORDER.indexOf(type);
  return i === -1 ? CATEGORY_ORDER.length + 100 : i;
};

// Comparator: canonical order, then alphabetical (short label) for any ties /
// unlisted types. Takes objects with a `.type` field.
export const byCategoryOrder = (a, b) =>
  categoryRank(a.type) - categoryRank(b.type)
  || formatCategoryLabelShort(a.type).localeCompare(formatCategoryLabelShort(b.type));

// Kosher-certification dropdown helpers — shared by the Add-Place form and the
// edit form so both order and label the supervision list identically. Sort by
// city, then country, then name; label as "City, Country — Name".
export const sortKosherCerts = (certs) =>
  [...(certs || [])].sort((a, b) =>
    (a.city || '').localeCompare(b.city || '')
    || (a.country || '').localeCompare(b.country || '')
    || (a.name || '').localeCompare(b.name || '')
  );

export const certOptionLabel = (c) => {
  const loc = [c.city, c.country].filter(Boolean).join(', ');
  return loc ? `${loc} — ${c.name}` : c.name;
};

// Split a free-text supervision string into individual certifying bodies,
// cert-aware so we don't cut a name that itself contains "and"/"&" (e.g.
// "Orthodox Rabbinical Board of Broward and Palm Beach Counties - ORB").
// Splits on commas/semicolons first; only breaks a segment on "and"/"&" when
// the whole segment isn't a known cert AND the pieces are. `certNames` is an
// array or Set of known certification names. Unrecognised segments stay whole.
// THE NAMES A CERTIFIER ANSWERS TO. House convention writes a record as
// "Latin (Other)" — the Hebrew half for an Israeli body, the acronym for
// "Scroll K (Vaad Hakashrus of Denver)", the expansion for "Jersey Shore
// Orthodox Rabbinate (JSOR)". But a place's free-text supervision names
// only ONE of those halves, and matching required the whole string, so the
// certifier's name sat on the page as plain text while its logo appeared
// right above it, linked. 26 certifiers are named that way and up to 487
// places mention one of their halves.
export const certAliases = (name) => {
  const full = String(name || '').trim();
  if (!full) return [];
  const out = [full];
  const m = full.match(/^(.*?)\s*\(([^()]*)\)\s*$/);
  if (m) {
    if (m[1].trim()) out.push(m[1].trim());
    if (m[2].trim()) out.push(m[2].trim());
  }
  return [...new Set(out.map((x) => x.toLowerCase()))];
};

// One token from a supervision string → the certifier it names, by any of
// those forms. Use this rather than comparing to c.name.
export const findCertByToken = (token, certs) => {
  const t = String(token || '').trim().toLowerCase();
  if (!t || !Array.isArray(certs)) return null;
  return certs.find((c) => certAliases(c.name).includes(t)) || null;
};

export const splitSupervision = (str, certNames) => {
  const known = certNames instanceof Set
    ? certNames
    : new Set((certNames || []).flatMap((n) => certAliases(n)));
  const segs = String(str || '').split(/\s*[,;]\s*/).map((t) => t.trim()).filter(Boolean);
  const out = [];
  for (const seg of segs) {
    if (known.has(seg.toLowerCase())) { out.push(seg); continue; }   // whole segment is a cert
    const sub = seg.split(/\s+and\s+|\s+&\s+/i).map((t) => t.trim()).filter(Boolean);
    if (sub.length > 1 && sub.some((t) => known.has(t.toLowerCase()))) out.push(...sub);
    else out.push(seg);  // unknown name → keep whole, never cut it
  }
  return out;
};

// Map a place to the certifier entities that certify it, so we can show their
// logos. `certs` is the kosherCertifications() list (each has id, name, slug,
// main_image_url). Prefers the STABLE id link (`certifierIds` =
// est.supervision_certifier_ids) so logos survive a certifier being renamed;
// falls back to whole-token matching of the free-text `supervision` for any
// certifier not yet represented by an id (legacy rows / one-offs). Returns the
// matched cert objects in order, de-duplicated.
export const matchSupervisionCerts = (supervision, certs, certifierIds) => {
  if (!Array.isArray(certs) || certs.length === 0) return [];
  const byId = new Map(certs.map((c) => [String(c.id), c]));
  // Every form of every name, so free text that says "Badatz Netanya" finds
  // the record called "Badatz Netanya (בד\"ץ בהידור הכשרות)".
  const byName = new Map();
  certs.forEach((c) => certAliases(c.name).forEach((a) => { if (!byName.has(a)) byName.set(a, c); }));
  const seen = new Set();
  const out = [];
  // ID-first (rename-proof).
  if (Array.isArray(certifierIds)) {
    for (const id of certifierIds) {
      const c = byId.get(String(id));
      if (c && !seen.has(c.id)) { seen.add(c.id); out.push(c); }
    }
  }
  // Name fallback for certifiers named in free text but not (yet) id-linked.
  if (supervision) {
    for (const tok of splitSupervision(supervision, [...byName.keys()])) {
      const c = byName.get(String(tok).trim().toLowerCase());
      if (c && !seen.has(c.id)) { seen.add(c.id); out.push(c); }
    }
  }
  return out;
};

// The "Certified by" line, token by token, each with the certifier it links to.
//
// THE TEXT IS IN HEBREW AND THE CERTIFIER IS FILED IN ENGLISH. A place in
// Kiryat Ono says «מועצה דתית קריית אונו» and the record it points at is
// called "Kiryat Ono Rabbinate", so matching by name finds nothing: the logo
// appeared, because the logo is matched by the stable id, and the name sat
// there as dead text. 3,445 places were like that.
//
// So the id link is used to rescue the token the text could not match: when
// one token is left over and exactly one linked certifier is unclaimed, that
// token IS that certifier. The words stay the source's own — we add the link,
// we do not translate the line.
//
// AND HEBREW JOINS TWO NAMES WITH A LETTER, not a comma: «רבנות ירושלים
// ובד"ץ העדה החרדית» is Jerusalem Rabbinate AND the Edah HaChareidis, one
// string, two bodies, and the ו is glued to the second name. Where a single
// leftover token splits on that ו into exactly as many pieces as there are
// unclaimed certifiers, it is split and paired in order — the importers
// linked the ids in the order the text names them. Any other shape is left
// as plain text: a link to the wrong beit din is worse than no link.
export const linkSupervisionTokens = (supervision, certs, certifierIds) => {
  const lista = Array.isArray(certs) ? certs : [];
  const sup = String(supervision || '').trim();
  const collegati = matchSupervisionCerts(null, lista, certifierIds);
  if (!sup) return collegati.map((c) => ({ text: c.name, cert: c }));

  const tokens = splitSupervision(sup, lista.map((c) => c.name));
  let presi = tokens.map((t) => findCertByToken(t, lista));
  const liberi = () => collegati.filter((c) => !presi.some((m) => m && m.id === c.id));

  let orfani = presi.reduce((n, m) => n + (m ? 0 : 1), 0);
  let parti = tokens;
  // un solo pezzo senza nome e più certificatori liberi: prova il taglio sulla ו
  if (orfani === 1 && liberi().length > 1) {
    const i = presi.findIndex((m) => !m);
    const pezzi = parti[i].split(/\s+ו(?=["'׳״]?[֐-׿])/).map((s) => s.trim()).filter(Boolean);
    if (pezzi.length === liberi().length) {
      parti = [...parti.slice(0, i), ...pezzi, ...parti.slice(i + 1)];
      presi = parti.map((t) => findCertByToken(t, lista));
      orfani = presi.reduce((n, m) => n + (m ? 0 : 1), 0);
    }
  }
  // tanti pezzi orfani quanti certificatori liberi: si accoppiano in ordine
  const resto = liberi();
  if (orfani === resto.length && orfani > 0) {
    let k = 0;
    presi = presi.map((m) => (m || resto[k++]));
  }
  return parti.map((text, i) => ({ text, cert: presi[i] || null }));
};

// THE KINDS OF PLACE THAT HAPPEN ON A DATE rather than being open all year.
// A Pesach hotel, a Chanukah ski week, a cruise: each runs once, between two
// dates, and the date is the first thing anyone asks about it. A restaurant
// has opening hours instead, and asking it for a start and end date would be
// noise — so the date fields, the date filter and the date sort all appear
// only for these.
export const DATED_TYPES = ['kosher_vacation', 'kosher_cruise'];

// "6–13 December 2026", or "3 April – 12 April 2027" when the months differ,
// or "from 6 December 2026" when only one end is known. Returns '' when there
// is no date at all, so a caller can simply render it.
export const formatDateRange = (from, to, locale = 'en-GB') => {
  const parse = (v) => {
    const d = v ? new Date(String(v).slice(0, 10)) : null;
    return d && !Number.isNaN(d.getTime()) ? d : null;
  };
  const a = parse(from);
  const b = parse(to);
  if (!a && !b) return '';
  const day = (d) => d.toLocaleDateString(locale, { day: 'numeric' });
  const monthYear = (d) => d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  const full = (d) => `${day(d)} ${monthYear(d)}`;
  if (a && !b) return `from ${full(a)}`;
  if (!a && b) return `until ${full(b)}`;
  // Same month: "6–13 December 2026" rather than repeating the month twice.
  if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) {
    return `${day(a)}–${day(b)} ${monthYear(a)}`;
  }
  if (a.getFullYear() === b.getFullYear()) {
    return `${day(a)} ${a.toLocaleDateString(locale, { month: 'long' })} – ${full(b)}`;
  }
  return `${full(a)} – ${full(b)}`;
};

// Categories that carry a kosher supervision (the Add-Place form shows the
// supervision dropdown only for these).
export const SUPERVISED_TYPES = [
  'kosher_restaurant', 'kosher_eatery', 'kosher_store', 'kosher_catering',
  'kosher_hotel', 'kosher_vacation', 'shabbat_meal', 'kosher_event_venue',
];

// Secondary tag — community for synagogues.
//
// QUESTA LISTA E' UN CANCELLO, NON UN SUGGERIMENTO. ListSidebar filtra le
// risposte di /communities contro questa lista (`onlyIn`), cosi' un valore
// sbagliato finito in un record non diventa una voce di menu. Il rovescio e'
// che un valore GIUSTO che non sta qui sparisce dal menu anche se in archivio
// ce ne sono centinaia: con le sole quattro voci di prima il visitatore non
// poteva scegliere Consistoire (422 schede), Ashkenazi (173), Sephardi (156),
// Yemenite (131 fra le tre grafie) o Edot HaMizrach (50). Erano nei dati e
// non nel menu.
//
// Menachem, 3/11/2026: «uno puo' scegliere fra Chabad o altre, anche altre
// che sono listate, come per esempio Young Israel». Young Israel sta qui
// anche se in archivio ne abbiamo solo undici: una voce che non esiste nei
// dati non compare (il menu e' l'INCROCIO fra questa lista e cio' che
// /communities trova), quindi si accende da sola quando le schede arrivano.
//
// Il filtro e' un LIKE: scegliere «Yemenite» prende anche «Yemenite (Baladi)»
// e «Yemenite (Shami)», che restano come scelta piu' stretta.
export const COMMUNITY_OPTIONS = {
  synagogue_shul: [
    // Reti e federazioni
    'Chabad', 'Young Israel', 'United Synagogue', 'Federation of Synagogues',
    'Consistoire',
    // Edot
    'Ashkenazi', 'Sephardi', 'Edot HaMizrach', 'Yemenite',
    'Yemenite (Baladi)', 'Yemenite (Shami)', 'Yerushalmi',
    'Moroccan', 'Persian', 'Bukharian', 'Ethiopian', 'Georgian',
    // Aggiunte il 4/11/2026, chieste da Menachem leggendo i nusachim di
    // Petach Tikva. BAVLI E IRAQI SONO DUE VOCI, non una: il registro
    // scrive «בבלי» per gli uni e «עירקי» per gli altri, e chi si chiama
    // in un modo non si chiama nell'altro — unirli sarebbe decidere al
    // posto loro. Lo stesso vale per i due riti teimani, che erano gia'
    // qui sopra e che il motore ora distingue davvero.
    // Le altre quattro le produce gia' l'importazione dei registri e
    // mancavano dall'elenco: una comunita' che l'archivio scrive ma il
    // menu non offre non si puo' ne' scegliere a mano ne' filtrare.
    'Bavli', 'Iraqi', 'Libyan', 'Indian', 'Tunisian', 'North African',
    // Chassidut. I tre col nome proprio li ha chiesti Menachem il 3/11/2026,
    // e il motivo si vede ad Arad: il consiglio religioso ne elenca
    // trentasette di Gur su cinquantanove, e lì «Chassidic» non distingue
    // niente. «Chassidic» resta per i gruppi che non hanno una voce loro —
    // Belz, Satmar, Sanz, Karlin, Slonim, Bobov, Sadigura.
    'Chassidic', 'Gur', 'Vizhnitz', 'Breslov',
    'Independent',
  ],
};

// Kosher + Shabbat amenities for hotels, accommodations, and vacation rentals.
// (Variable name 'SHABBAT_AMENITIES' is historical — the column is also
// `shabbat_amenities` JSONB. The list now covers kosher food + Jewish life
// amenities too. Each entry: slug (DB value), label (UI text), icon (emoji).)
// Each entry carries a `group` so the editor and detail page can render the
// amenities under labeled sections (see AMENITY_GROUPS) and so the two headline
// signals — "kosher food" and "Shabbat-friendly" — can be derived from them.
//   food        = served kosher food (breakfast, meals, restaurant)
//   kitchen     = self-catering / apartment kosher kitchen (utensils, sinks…)
//   shabbat     = Shabbos-observance infrastructure (elevator, keys, plata…)
//   jewish_life = nearby Jewish infrastructure (shul, mikveh, eruv…)
export const SHABBAT_AMENITIES = [
  // Food (served)
  { slug: 'kosher_breakfast',          label: 'Kosher breakfast',              icon: '🥐',  group: 'food' },
  { slug: 'kosher_breakfast_on_request', label: 'Kosher breakfast on request', icon: '🥐',  group: 'food' },
  { slug: 'kosher_friendly_breakfast', label: 'Kosher-friendly breakfast',     icon: '🥐',  group: 'food' },
  { slug: 'kosher_restaurant_onsite',  label: 'Kosher restaurant on-site',     icon: '🍽️',  group: 'food' },
  { slug: 'shabbat_meals',             label: 'Shabbat meals (Fri night + Shabbat lunch)', icon: '🍷', group: 'food' },
  { slug: 'mehadrin',                  label: 'Mehadrin / Glatt certified',    icon: '✡️',  group: 'food' },
  { slug: 'cholov_yisrael',            label: 'Cholov Yisrael available',      icon: '🥛',  group: 'food' },
  // Kosher kitchen (self-catering / apartments)
  { slug: 'kosher_kitchen',            label: 'Kosher kitchen (utensils provided)', icon: '🍳', group: 'kitchen' },
  { slug: 'separate_meat_dairy',       label: 'Separate meat & dairy utensils', icon: '🍴', group: 'kitchen' },
  { slug: 'separate_sinks_ovens',      label: 'Separate meat/dairy sinks or ovens', icon: '🚰', group: 'kitchen' },
  { slug: 'pesach_kitchen',            label: 'Kosher-for-Passover kitchen available', icon: '🫓', group: 'kitchen' },
  { slug: 'kitchenette_byo',           label: 'Kitchenette (kasher yourself / BYO)', icon: '🧑‍🍳', group: 'kitchen' },
  // Shabbat
  { slug: 'shabbat_elevator',          label: 'Shabbat elevator',              icon: '🛗',  group: 'shabbat' },
  { slug: 'shabbos_lamp',              label: 'Shabbos lamp',                  icon: '🪔',  group: 'shabbat' },
  { slug: 'pre_set_candles',           label: 'Pre-set Shabbat candles',       icon: '🕯️',  group: 'shabbat' },
  { slug: 'regular_key',               label: 'Regular key (mechanical)',      icon: '🔑',  group: 'shabbat' },
  { slug: 'no_automatic_lights',       label: 'No automatic lights',           icon: '💡',  group: 'shabbat' },
  { slug: 'fridge_closet_light_off',   label: 'Fridge / closet light can be disabled', icon: '🧊', group: 'shabbat' },
  { slug: 'shabbat_light_timer',       label: 'Shabbat timer for lights (Shabbos clock)', icon: '⏲️', group: 'shabbat' },
  { slug: 'staff_opens_room',          label: 'Staff opens room for guests',   icon: '🛎️',  group: 'shabbat' },
  { slug: 'no_automatic_doors',        label: 'No automatic doors',            icon: '🚪',  group: 'shabbat' },
  { slug: 'manual_shabbat_entrance',   label: 'Separate manual entrance (regular, non-automatic door)', icon: '🚪',  group: 'shabbat' },
  { slug: 'staff_opens_doors',         label: 'Staff opens automatic doors',   icon: '🚪',  group: 'shabbat' },
  // Menachem, 23/10/2026: «se lo staff accende i sensori va anche bene ma
  // si deve mettere una checkbox nel sito». Terza della famiglia «lo fa il
  // personale», accanto all'apertura della camera e della porta: un albergo
  // con i sensori resta utilizzabile se qualcuno li disattiva prima dello
  // Shabbat. Senza questa casella l'unica risposta registrabile era «non ci
  // sono sensori», e un albergo che si organizza risultava uguale a uno che
  // non fa niente.
  { slug: 'staff_disables_sensors',   label: 'Staff disables the sensor lights for Shabbat', icon: '💡',  group: 'shabbat' },
  // UNA CASELLA CHE DICE DI NO.
  //
  // Menachem, 27/10/2026: «deve esserci una pillola che però diciamo è
  // rossa, o comunque che si vede che è qualcosa che non va bene, che
  // dice luci automatiche nelle parti comuni».
  //
  // Tutte le altre caselle qui sopra dicono una cosa buona, e la
  // scheda le mostra tutte uguali. Ma gli alberghi con i sensori nei
  // corridoi li togliamo dagli elenchi, e allora la loro scheda la
  // vede solo chi ci arriva da un link o da Google — una persona che
  // sta valutando quell'albergo proprio adesso. A quella persona
  // l'informazione serve addosso al nome, non in fondo a una nota.
  //
  // `warning: true` la fa rossa e la mette in cima, e in piu' annulla
  // l'etichetta «Shabbat friendly»: e' una risposta diretta
  // dell'albergo, quindi batte anche la presunzione che vale per chi
  // ha un'hashgacha.
  { slug: 'automatic_common_lights', label: 'Automatic lights in common areas', icon: '⚠️', group: 'shabbat', warning: true },
  { slug: 'plata',                     label: 'Plata (hotplate)',              icon: '🔥',  group: 'shabbat' },
  { slug: 'water_urn',                 label: 'Water urn',                     icon: '🫖',  group: 'shabbat' },
  { slug: 'shabbos_belt',              label: 'Shabbos belt',                  icon: '🎗️',  group: 'shabbat' },
  // Jewish life
  { slug: 'mezuzot',                   label: 'Mezuzot on doorposts',          icon: '📜',  group: 'jewish_life' },
  { slug: 'mikveh_onsite',             label: 'Mikveh on-site',                icon: '🛁',  group: 'jewish_life' },
  { slug: 'mikveh_walking',            label: 'Mikveh walking distance',       icon: '🚶',  group: 'jewish_life' },
  { slug: 'synagogue_onsite',           label: 'Synagogue on-site',            icon: '🕍',  group: 'jewish_life' },
  { slug: 'synagogue_walking',         label: 'Synagogue walking distance',    icon: '🕍',  group: 'jewish_life' },
  { slug: 'inside_eruv',               label: 'Inside an eruv',                icon: '🪢',  group: 'jewish_life' },
  { slug: 'siddur_in_room',            label: 'Siddur / Tehillim in room',     icon: '📖',  group: 'jewish_life' },
  { slug: 'hebrew_staff',              label: 'Hebrew-speaking staff',         icon: '🗣️',  group: 'jewish_life' },
];

// Section headers for the grouped amenities editor + detail display.
export const AMENITY_GROUPS = [
  { key: 'food',        label: '🍽️ Kosher food served' },
  { key: 'kitchen',     label: '🍳 Kosher kitchen (self-catering)' },
  { key: 'shabbat',     label: '🕯️ Shabbat observance' },
  { key: 'jewish_life', label: '🕍 Jewish life nearby' },
];

// Explicit "kosher food" tier for lodging — the headline distinction between a
// place that just supports Shabbos and one where you can actually eat kosher.
// Ordered weakest → strongest; `rank` lets inference pick the strongest match.
export const KOSHER_FOOD_LEVELS = [
  { slug: 'none',             label: 'No kosher food — self-catering',        short: 'Self-catering',    icon: '🚫', rank: 0 },
  { slug: 'kosher_kitchen',   label: 'Kosher kitchen (utensils provided)',    short: 'Kosher kitchen',   icon: '🍳', rank: 1 },
  { slug: 'kosher_breakfast', label: 'Kosher breakfast',                      short: 'Kosher breakfast', icon: '🥐', rank: 2 },
  { slug: 'kosher_meals',     label: 'Kosher meals available',                short: 'Kosher meals',     icon: '🍷', rank: 3 },
  { slug: 'kosher_onsite',    label: 'Kosher restaurant / kitchen on-site',   short: 'Kosher on-site',   icon: '🍽️', rank: 4 },
];

export const kosherFoodMeta = (slug) => KOSHER_FOOD_LEVELS.find((l) => l.slug === slug) || null;

// Infer the kosher-food tier from checked amenities (strongest wins). Used to
// default the explicit field for legacy listings so nothing shows up blank.
export const inferKosherFoodLevel = (est) => {
  const a = Array.isArray(est && est.shabbat_amenities) ? est.shabbat_amenities : [];
  if (a.includes('kosher_restaurant_onsite')) return 'kosher_onsite';
  if (a.includes('shabbat_meals') || a.includes('kosher_meals')) return 'kosher_meals';
  if (a.includes('kosher_breakfast') || a.includes('kosher_breakfast_on_request') || a.includes('kosher_friendly_breakfast')) return 'kosher_breakfast';
  if (a.includes('kosher_kitchen') || a.includes('separate_meat_dairy') || a.includes('separate_sinks_ovens')) return 'kosher_kitchen';
  return 'none';
};

// The effective tier = explicit field if the owner set one, else inferred.
export const effectiveKosherFood = (est) => {
  const explicit = est && est.kosher_food_level;
  return explicit && explicit !== '' ? explicit : inferKosherFoodLevel(est);
};

// "Shabbat-friendly" = has any Shabbat-observance or nearby-Jewish-life amenity.
// «SHABBAT FRIENDLY»: TRE PROBLEMI, OGNUNO ASSENTE O RISOLTO.
//
// Menachem, 23/10/2026: «deve essere o che l'albergo dice che il loro
// personale e disponibile a fare tutte le cose — i sensori, aprire la porta
// della camera, aprire la porta dell'albergo — oppure serve che non ci siano
// sensori per la luce, la porta sia manuale e non ci siano altri problemi».
//
// Sono tre ostacoli, e per ognuno ci sono due modi di superarlo: o
// l'ostacolo non c'e, o qualcuno lo toglie di mezzo. Vanno superati TUTTI E
// TRE — una chiave meccanica non serve a niente se poi la porta d'ingresso
// e automatica e nessuno la apre.
//
//   in camera    chiave meccanica   oppure  il personale apre
//   in albergo   porta manuale      oppure  il personale apre
//   le luci      niente sensori     oppure  il personale li disattiva
//
// Si possono mescolare: chiave meccanica + personale che apre il portone +
// niente sensori e una combinazione valida, perche ogni ostacolo e comunque
// superato. Richiedere che siano tutti risolti nello stesso modo
// escluderebbe alberghi perfettamente utilizzabili.
//
// QUELLO CHE NON BLOCCA, e deliberatamente. Il frigo con la luce che non si
// puo togliere — «quello e una cosa che si puo vivere», dice Menachem: ci si
// porta un nastro adesivo, o si lascia il frigo chiuso. Stessa cosa per
// l'ascensore di Shabbat, la plata, il bollitore e le candele: sono comodita
// che migliorano il soggiorno, non cose che impediscono di passarci Shabbat.
// Restano tutte sulla scheda, dove uno le legge; semplicemente non decidono
// l'etichetta.
//
// E NON BASTA PIU' LA VICINANZA A UNA SINAGOGA. Prima bastava, e 35 alberghi
// su 65 portavano l'etichetta per quello: il Mount Nelson di Citta del Capo
// era «Shabbat friendly» con spuntato soltanto `synagogue_walking`. Dove sta
// l'albergo e un'informazione sul quartiere, non sull'albergo.
const SF_CAMERA = ['regular_key', 'staff_opens_room'];
const SF_INGRESSO = ['no_automatic_doors', 'manual_shabbat_entrance', 'staff_opens_doors'];
const SF_LUCI = ['no_automatic_lights', 'staff_disables_sensors'];
export const SHABBAT_FRIENDLY_REQUISITI = [SF_CAMERA, SF_INGRESSO, SF_LUCI];
// UNA HASHGACHA VALE I TRE OSTACOLI.
//
// Menachem, 23/10/2026: «gli alberghi che hanno una shgaha ovviamente sono
// gia' Shabbat friendly». E' vero, e non e' una scorciatoia: un albergo
// sotto hashgacha non puo' essere certificato se di Shabbat si cucina o se
// il personale ebreo lavora, quindi l'albergo Shabbat lo osserva per
// intero - ascensore, chiavi, timer delle luci vengono da soli. Chi
// rilascia il certificato ha gia' controllato quello che stiamo chiedendo
// noi, e molto piu' da vicino.
//
// E RISOLVE ISRAELE SENZA UNA REGOLA PER ISRAELE. La regola dei tre
// ostacoli e' nata guardando Parigi e Budapest, dove un albergo che si
// organizza per Shabbat e' l'eccezione. In Israele e' il contrario, e
// applicata li' tale e quale nascondeva 204 alberghi su 380 - il Waldorf
// Astoria di Gerusalemme, il Dan Accadia di Herzliya, posti dove Shabbat
// lo passa mezza citta'. Con l'hashgacha quei 204 rientrano da soli,
// perche' ce l'hanno tutti: non serve scrivere «in Israele vale un'altra
// regola», che sarebbe arbitrario e andrebbe mantenuto a mano per sempre.
//
// RESTA DA CONFERMARE, e la conferma e' partita: una lettera a ognuno di
// loro per farsi dire COME funziona (chiavi, portone, luci), cosi' la
// scheda dice la cosa precisa invece della presunzione. Finche' non
// rispondono la presunzione regge, perche' sbagliare per eccesso qui
// costa poco: chi va in un albergo con l'hashgacha di un rabbinato
// cittadino non si trova in difficolta' di Shabbat.
//
// MA L'HECHSHER DEV'ESSERE SULL'ALBERGO, NON SULLA COLAZIONE.
//
// Fra i 17 con hashgacha fuori da Israele ce ne sono quattro ad Amsterdam -
// Ibis Budget, Holiday Inn Express, NH, Adagio - tutti con «Bet Din Tzedek
// of Amsterdam», e tutti con una sola dotazione: `kosher_breakfast`. Quel
// certificato sta sul vassoio della colazione, non sull'albergo. Un Ibis
// Budget non diventa osservante perche' un Beth Din gli approva il pane, e
// avra' le tessere magnetiche e i sensori di ogni Ibis. E' la stessa
// distinzione che vale per i negozi: l'hechsher puo' stare sulla MERCE
// invece che sul locale.
//
// Il segnale che li separa e' gia' nei dati: SONO DI CATENA. Gli alberghi
// ebraici veri - Croft Court a Londra, King David a Berlino e a Praga, Park
// Avenue, Neman a Roma - non appartengono a nessuna catena, perche' sono
// imprese di famiglia nate per quella clientela.
//
// In Israele vale il contrario e la catena non dice niente: anche Leonardo,
// Isrotel e Dan sono osservanti. Non e' una deroga geografica a capriccio,
// e' che il certificato significa due cose diverse nei due posti - in
// Israele la kashrut di un albergo copre l'ESERCIZIO, e un albergo che di
// Shabbat cucina non la ottiene; fuori, un Beth Din certifica spesso solo
// un servizio di catering dentro un albergo qualunque.
const diCatena = (est) => !!(est && typeof est.chain === 'string' && est.chain.trim());
const haHashgacha = (est) => !!(est && typeof est.supervision === 'string' && est.supervision.trim())
  && (est.country === 'Israel' || !diCatena(est));

// Le caselle che dicono di no. Una sola basta a togliere l'etichetta.
export const AVVISI = SHABBAT_AMENITIES.filter((a) => a.warning).map((a) => a.slug);

export const isShabbatFriendly = (est) => {
  const a = Array.isArray(est && est.shabbat_amenities) ? est.shabbat_amenities : [];
  // UN AVVISO BATTE ANCHE L'HASHGACHA. La scorciatoia dell'hashgacha e'
  // una presunzione — «chi certifica ha gia' guardato queste cose» — e
  // una presunzione cade davanti a una risposta scritta dell'albergo.
  if (a.some((s) => AVVISI.includes(s))) return false;
  if (haHashgacha(est)) return true;
  if (!a.length) return false;
  return SHABBAT_FRIENDLY_REQUISITI.every((gruppo) => gruppo.some((s) => a.includes(s)));
};

// Quali dei tre ostacoli restano scoperti. Serve al pannello: a un
// proprietario che ha spuntato due caselle su tre si puo dire quale manca,
// invece di lasciargli indovinare perche l'etichetta non compare.
export const shabbatFriendlyMancanti = (est) => {
  const av = Array.isArray(est && est.shabbat_amenities) ? est.shabbat_amenities : [];
  // Se c'e' un avviso, quello E' il motivo: dirlo per primo, se no il
  // proprietario spunta le tre caselle e non capisce perche'
  // l'etichetta non torna.
  const acceso = SHABBAT_AMENITIES.filter((x) => x.warning && av.includes(x.slug)).map((x) => x.label);
  if (acceso.length) return acceso;
  if (haHashgacha(est)) return [];
  const a = av;
  const nomi = ['Getting into the room', 'Getting into the building', 'Lights on a sensor'];
  return SHABBAT_FRIENDLY_REQUISITI
    .map((g, i) => (g.some((s) => a.includes(s)) ? null : nomi[i]))
    .filter(Boolean);
};

// Establishment types that support the Shabbat amenities field.
export const SUPPORTS_SHABBAT_AMENITIES = [
  'kosher_hotel',
  'kosher_vacation',
  'shabbat_friendly_accommodation',
  'accommodation', // legacy slug, kept just in case
];

// Curated hotel-chain / brand suggestions for lodging listings. Offered via a
// datalist so an owner can pick one OR type a chain that isn't listed (the
// `chain` column is free text). Not exhaustive — just the common ones + the
// Israeli chains kosher travelers look for.
export const HOTEL_CHAINS = [
  'Marriott', 'Hilton', 'Hyatt', 'InterContinental (IHG)', 'Holiday Inn', 'Crowne Plaza',
  'Accor', 'Sofitel', 'SO/', 'Novotel', 'Mercure', 'Ibis', 'Pullman',
  'Radisson', 'Wyndham', 'Best Western', 'Four Seasons', 'Ritz-Carlton', 'Kempinski',
  'Sheraton', 'Westin', 'Fairmont', 'Mandarin Oriental', 'Shangri-La', 'Rocco Forte',
  'Jumeirah', 'Rotana', 'NH Hotels', 'Meliá', 'Barceló', 'Corinthia', 'Anantara',
  'Leonardo', 'Warwick', 'Adagio', 'Belmond', 'Clarion', 'Danubius', 'Eurostars',
  'Taj Hotels', 'Cabinn',
  // Israel
  'Dan Hotels', 'Isrotel', 'Fattal', 'Herods', 'Prima', 'Rimonim', 'Brown Hotels', 'Atlas',
  // South Africa
  'Premier Hotels', 'SunSquare', 'The Capital Hotels',
];

// Curated, shared cuisine list — the ONLY values pickable (admins can add new
// ones). Keep this in sync with scripts/normalize-cuisines.js on the backend.
export const CUISINE_LIST = [
  // Regional / style
  // «Lebanese» e' entrata il 2/11/2026, scrivendo di Snubar a Milano: un
  // kosher libanese che non si poteva etichettare per quello che e'.
  // Inventarla al volo nel database sarebbe stato peggio di lasciarla
  // vuota — un valore che i filtri non conoscono fa sparire la scheda
  // invece di farla trovare — quindi entra qui prima, e nelle schede dopo.
  'Israeli', 'Ashkenazi', 'Roman Jewish', 'Italian', 'Mediterranean', 'Moroccan', 'Lebanese', 'Georgian',
  'Indian', 'Chinese', 'Asian', 'Japanese', 'American', 'Mexican', 'French',
  // Dishes / formats
  'Pizza', 'Pasta', 'Sushi', 'Poké', 'Burgers', 'Grill', 'Steakhouse', 'Falafel', 'Shawarma',
  'Hummus', 'Shakshuka', 'Salads', 'Sandwiches', 'Fish', 'Fish & Chips',
  // Bakery / sweet / other outlets
  'Bakery', 'Pastry shop', 'Café', 'Ice Cream', 'Frozen Yogurt', 'Deli', 'Butcher',
  // Dietary — the Meat/Dairy/Parve KIND lives in the subtype field, not here.
  'Vegetarian', 'Fast Food', 'Catering',
];
export const CUISINE_OPTIONS = {
  kosher_restaurant: CUISINE_LIST,
  kosher_eatery: CUISINE_LIST,
  kosher_catering: CUISINE_LIST,
  kosher_event_venue: CUISINE_LIST,
};

// Custom flag URL overrides (used when flagcdn.com doesn't have it or you prefer a different source)
const FLAG_OVERRIDES = {
  Gibraltar: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/02/Flag_of_Gibraltar.svg/1280px-Flag_of_Gibraltar.svg.png',
};

// Returns a flag image URL for a country (custom override or flagcdn.com default)
export const flagUrl = (country, size = 'h20') => {
  if (FLAG_OVERRIDES[country]) return FLAG_OVERRIDES[country];
  const code = countryCode(country);
  return code ? `https://flagcdn.com/${size}/${code}.png` : null;
};

// ---------- PWA push notifications ----------
export const pushSupported = () =>
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator
  && typeof window !== 'undefined' && 'PushManager' in window && 'Notification' in window;

function urlB64ToUint8Array(base64) {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const s = (base64 + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(s);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Current state: 'unsupported' | 'denied' | 'on' | 'off'.
export async function pushStatus() {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return sub ? 'on' : 'off';
  } catch (e) { return 'off'; }
}

// Ask permission, subscribe, and register the subscription with the server.
// Throws 'unsupported' | 'not-configured' | 'denied' on failure.
export async function enablePush() {
  if (!pushSupported()) throw new Error('unsupported');
  const cfg = await request('/api/push/public-key');
  if (!cfg || !cfg.enabled || !cfg.key) throw new Error('not-configured');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('denied');
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(cfg.key) });
  await request('/api/push/subscribe', { method: 'POST', body: JSON.stringify(sub.toJSON()) });
  return true;
}

export async function disablePush() {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) { await request('/api/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint: sub.endpoint }) }).catch(() => {}); await sub.unsubscribe(); }
  } catch (e) { /* ignore */ }
}

// Set/clear the app-icon unread badge (Badging API; no-op where unsupported).
export async function setAppBadge(count) {
  try {
    if (typeof navigator === 'undefined') return;
    if (count > 0 && navigator.setAppBadge) await navigator.setAppBadge(count);
    else if (navigator.clearAppBadge) await navigator.clearAppBadge();
  } catch (e) { /* ignore */ }
}

// Countries where the leading 0 (trunk prefix) is KEPT when adding country code
// (Italy is the famous exception)
const KEEP_LEADING_ZERO = new Set(['Italy', 'Vatican City']);

// Clean a phone number for use in tel: and wa.me URLs
// Strips spaces, dashes, parentheses, dots; keeps the leading +
export const cleanPhone = (phone) => {
  if (!phone) return '';
  return phone.replace(/[\s\-().]/g, '').replace(/^00/, '+');
};

// UN NOME UTENTE DI WHATSAPP NON E' UN NUMERO.
//
// Menachem, 29/10/2026, su Beth Chabad Israeli a Vaughan: il contatto e'
// «@Rabbi.Landa», e la scheda ci appiccicava davanti il prefisso del
// Canada — «+1@RabbiLanda» — e offriva il bottone CHIAMA. Quel bottone
// non puo' funzionare: un nome utente non si compone sulla tastiera del
// telefono.
//
// Da quando WhatsApp ha i nomi utente, chi vuole puo' pubblicare quello
// invece del numero. wa.me li riconosce da solo: provato il 29/10/2026,
// https://wa.me/Rabbi.Landa rimanda a
// api.whatsapp.com/send/?username=Rabbi.Landa&type=username — quindi per
// SCRIVERE basta il nome senza la chiocciola, ed e' solo il prefisso e la
// chiamata che vanno tolti.
//
// SI RICONOSCE DALLA CHIOCCIOLA, E SOLO DA QUELLA. La prima stesura diceva
// «una lettera qualsiasi»: in archivio ci sono 77 telefoni con dentro una
// lettera — «416-222-7105 x255», «(905) 356-7200 ext. 772», «+972-3-762-5015
// (also WhatsApp)» — e sono numeri veri. Quella regola avrebbe tolto il
// bottone CHIAMA a tutti e 77 per sistemarne uno.
export const isWhatsAppHandle = (v) => String(v || '').trim().startsWith('@');

// Quante cifre ci sono: sotto le cinque non e' un numero da comporre. In
// archivio il campo telefono di qualche crociera dice soltanto «Israel».
const quanteCifre = (v) => (String(v || '').match(/\d/g) || []).length;

// Add the country prefix if missing. Returns phone in international format starting with +
//
// IL PREFISSO PUO' ESSERCI GIA', E ALLORA MANCA SOLO IL SEGNO.
//
// Menachem, 20/10/2026, su Chabad of Ein Hayam ad Ashkelon: «quando si
// schiaccia su chiamare o WhatsApp, da piu 972, poi 972, e questo e un
// problema». Il numero in archivio era «972-54-448-5471» — il prefisso c'e,
// manca il «+» — e qui davanti ci finiva un secondo +972: +972972544485471,
// che non chiama nessuno.
//
// Si riconosce cosi: le cifre cominciano col prefisso del paese, non con lo
// zero (lo zero e composizione locale, quindi quelle cifre sono il numero) e
// quello che resta e lungo abbastanza da essere un numero. Due eccezioni —
// in Nord America il prefisso «1» vale solo con dieci cifre dopo, e in Italia
// i cellulari cominciano per 3, quindi 393 859 8192 non e il prefisso 39
// seguito da 38598192.
export const phoneWithCountry = (phone, country) => {
  if (!phone) return '';
  // Un nome utente di WhatsApp non si chiama, e nemmeno la parola
  // «Israel»: niente prefisso, niente numero da comporre. Chi disegna il
  // bottone CHIAMA guarda questo risultato, quindi tornando vuoto il
  // bottone sparisce da solo.
  if (isWhatsAppHandle(phone) || quanteCifre(phone) < 5) return '';
  const cleaned = cleanPhone(phone);
  if (cleaned.startsWith('+')) return cleaned;
  const code = COUNTRY_PHONE_CODES[country];
  if (!code) return cleaned; // unknown country - return as-is
  const nudo = String(code).replace(/\D/g, '');
  if (nudo && !cleaned.startsWith('0') && cleaned.startsWith(nudo)) {
    const resto = cleaned.length - nudo.length;
    const minimo = country === 'Italy' ? 9 : 6;
    const gia = nudo === '1' ? resto === 10 : (resto >= minimo && resto <= 12);
    if (gia) return `+${cleaned}`;
  }
  const keepZero = KEEP_LEADING_ZERO.has(country);
  // For most countries strip leading 0; for Italy keep it
  const local = keepZero ? cleaned : cleaned.replace(/^0+/, '');
  return `${code}${local}`;
};

// Strip the + for WhatsApp (which uses raw digits), or hand wa.me the user
// name as it is — senza la chiocciola, che nel percorso non ci va.
export const phoneForWhatsApp = (phone, country) => {
  if (isWhatsAppHandle(phone)) return String(phone).trim().replace(/^@+/, '').replace(/\s+/g, '');
  return phoneWithCountry(phone, country).replace(/^\+/, '');
};

// DUE CAMPI CHE A VOLTE SONO DUE NUMERI DIVERSI.
//
// Menachem, 2/11/2026, su Beit Chana di Safed: «Not showing both numbers
// of Raskin». La scheda ne porta due — il fisso in `contact_phone` e il
// cellulare in `contact_whatsapp` — ma la pagina trattava quei due campi
// come DUE SCRITTURE DELLO STESSO NUMERO: ne sceglieva uno e buttava
// l'altro. E li sceglieva al contrario: l'elenco preferiva
// `contact_phone`, la scheda `contact_whatsapp`, quindi la stessa persona
// mostrava un numero diverso a seconda di dove la guardavi.
//
// Qui i campi diventano una LISTA: se dicono la stessa cosa resta un
// numero, se dicono due cose diverse restano due. Sono tre schede in
// tutto l'archivio — Beit Chana, Chabad di Istanbul, il David Citadel —
// ma un recapito che c'e' e non si vede e' peggio di uno che manca,
// perche' nessuno sa di doverlo cercare.
//
// SI CONFRONTANO LE ULTIME OTTO CIFRE, non la stringa: «054-5414567» e
// «+972 54-541-4567» sono lo stesso numero scritto in due modi, e
// contarli due volte sarebbe lo stesso errore al contrario. Otto e non di
// piu' perche' il prefisso del paese e lo zero di composizione locale
// cambiano da una scrittura all'altra; otto e non di meno perche' sotto
// si comincia a confondere numeri davvero diversi.
//
// I nomi utente di WhatsApp («@Rabbi.Landa») non entrano: si scrivono,
// non si chiamano, e il bottone per scrivere ce l'hanno gia'.
export const numeriContatto = (...campi) => {
  const visti = new Set();
  const fuori = [];
  for (const c of campi) {
    const s = String(c || '').trim();
    if (!s || isWhatsAppHandle(s) || quanteCifre(s) < 5) continue;
    const cifre = s.replace(/\D/g, '');
    const coda = cifre.slice(-8);
    if (visti.has(coda)) continue;
    visti.add(coda);
    fuori.push(s);
  }
  return fuori;
};

const getToken = () => localStorage.getItem('jwt_token');

// Build query string, skipping null/undefined/empty values
const buildQuery = (params) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') sp.append(k, v);
  });
  return sp.toString();
};

// A READ THAT FAILS GETS A SECOND CHANCE, AND A THIRD.
//
// Menachem, 20/10/2026: «why so many times I go in the website now and the
// database is not connected, just showing the website with zero places».
// That is what a cold or restarting API looks like from here: the page is
// static and loads fine, the first call to the server dies — a dropped
// connection, or a 502/503/504 from the proxy while the service comes back —
// and the list, having caught the error, draws itself empty. Nothing retries,
// so an outage of twenty seconds looks like a site with nothing in it.
//
// So a READ waits and asks again. The waits reach half a minute on purpose:
// measured on the live site, a deploy takes the API down for tens of
// seconds, and a backoff that gives up after seven shows the same empty
// page it was meant to prevent — I watched /kashrut-changes retry four
// times, collect four 502s and draw itself empty while the service was
// still coming back. Writes are never retried: a POST that timed out may
// well have gone through.
const TRANSITORIO = new Set([502, 503, 504, 429]);
const dormi = (ms) => new Promise((r) => { setTimeout(r, ms); });

const request = async (path, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const lettura = !options.method || String(options.method).toUpperCase() === 'GET';
  const attese = lettura ? [800, 2000, 4000, 8000, 15000] : [];
  let res;
  for (let tentativo = 0; ; tentativo += 1) {
    try {
      res = await fetch(`${API_URL}${path}`, { ...options, headers });
      if (!TRANSITORIO.has(res.status) || tentativo >= attese.length) break;
    } catch (e) {
      // La rete ha detto di no: nessuna risposta, niente da leggere.
      if (tentativo >= attese.length) throw e;
    }
    await dormi(attese[tentativo]);
  }

  // Self-heal a stale token: a 403 on an authenticated request usually means
  // the role was upgraded since login (e.g. the user became an owner), so the
  // token's role is behind the DB. Refresh the token via /me and retry once.
  if (res.status === 403 && token && !options._retried && path !== '/api/auth/me') {
    try {
      const meRes = await fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (meRes.ok) {
        const me = await meRes.json().catch(() => ({}));
        if (me && me.token && me.token !== token) {
          localStorage.setItem('jwt_token', me.token);
          return request(path, { ...options, _retried: true });
        }
      }
    } catch (_) { /* fall through and surface the original 403 */ }
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || `Request failed: ${res.status}`);
  return data;
};

// In-memory caches for the certifier list (see api.kosherCertifications).
let _certCache = { data: null, at: 0 };
let _certCacheCounts = { data: null, at: 0 };

export const api = {
  // Auth
  register: (body) => request('/api/auth/register', { method: 'POST', body: JSON.stringify(body) }),
  login: (email, password) =>
    request('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  // Exchange a magic-login token (from an owner email link) for a session.
  magicLogin: (token) =>
    request('/api/auth/magic-login', { method: 'POST', body: JSON.stringify({ token }) }),
  googleLogin: (credential) =>
    request('/api/auth/google', { method: 'POST', body: JSON.stringify({ credential }) }),
  forgotPassword: (email) =>
    request('/api/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token, newPassword) =>
    request('/api/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, newPassword }) }),
  verifyEmail: (token) =>
    request('/api/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) }),
  resendVerification: () =>
    request('/api/auth/resend-verification', { method: 'POST' }),
  me: () => request('/api/auth/me'),
  updateProfile: (body) => request('/api/auth/me', { method: 'PATCH', body: JSON.stringify(body) }),
  changePassword: (currentPassword, newPassword) =>
    request('/api/auth/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),

  // Categories & establishments
  categories: (params = {}) => {
    // FORWARD EVERYTHING, DROP ONE THING ON PURPOSE.
    //
    // This was a hand-written allowlist, and it fell behind twice. First it
    // sent country/city/area only, so searching "Calabria" — five results,
    // two synagogues, two attractions and a hotel — still offered all 29
    // categories with their worldwide counts; state, metro and search were
    // going out of the list page and being thrown away here. The fix added
    // those six names.
    //
    // Then "near me" arrived, and near_lat/near_lng/radius_km hit the same
    // wall: the page passed them, this discarded them, and the backend's
    // distance filter needs all three or it applies none. A list of four
    // places near the Rome ghetto sat under a menu reading "Bakery 2107".
    //
    // An allowlist that silently drops what it does not recognise will keep
    // doing this every time a filter is added. So the rule is inverted: send
    // what the caller passes, and name only what is deliberately withheld.
    //
    // `type` is withheld: the menu lists all categories in scope INCLUDING
    // the one currently chosen, so scoping it by that choice would leave a
    // menu of one.
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (k === 'type') return;
      if (v === undefined || v === null || v === '') return;
      qs.set(k, v);
    });
    const s = qs.toString();
    return request(`/api/categories${s ? `?${s}` : ''}`);
  },
  // How many places IN THE CURRENT VIEW each hechsher certifies, keyed by
  // certifier id. kosherCertifications() stays what it is — every certifier
  // with its logo, fetched once and memoised, because the result cards need
  // the logos whatever the filters say. This is the other half: the numbers
  // for where the visitor actually is. Same forward-everything rule as
  // categories(), and `type` is kept here rather than dropped, because the
  // hechsher menu does narrow with the chosen category.
  certifierCounts: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      qs.set(k, v);
    });
    const s = qs.toString();
    return request(`/api/certifier-counts${s ? `?${s}` : ''}`);
  },
  countries: (params = {}) => {
    const qs = buildQuery(params);
    return request(`/api/countries${qs ? `?${qs}` : ''}`);
  },
  cities: (params = {}) => {
    const qs = buildQuery(params);
    return request(`/api/cities${qs ? `?${qs}` : ''}`);
  },
  areas: (params = {}) => {
    const qs = buildQuery(params);
    return request(`/api/areas${qs ? `?${qs}` : ''}`);
  },
  // Metro-area anchors (radius filter). Optional { country }.
  metros: (params = {}) => {
    const qs = buildQuery(params);
    return request(`/api/metros${qs ? `?${qs}` : ''}`);
  },
  states: (params = {}) => {
    const qs = buildQuery(params);
    return request(`/api/states${qs ? `?${qs}` : ''}`);
  },
  cityGuide: (cc, city) => request(`/api/city-guide?cc=${encodeURIComponent(cc)}&city=${encodeURIComponent(city)}`),
  // Il nome del paese dal codice, per la pagina /it. Serve un giro al
  // server perche COUNTRY_CODE qui dentro copre 67 paesi su 111.
  countryByCode: (cc) => request(`/api/country?cc=${encodeURIComponent(cc)}`),
  // Fire-and-forget outbound-click ping (whatsapp/phone/website/booking).
  // keepalive lets it complete even as the click navigates the tab away.
  // I recapiti non arrivano con la scheda: si chiedono premendo un bottone.
  // Il perche' sta in travel-booking-backend/services/gate-contacts.js - in
  // breve, finche' telefono ed email stanno nell'HTML un assistente li legge
  // e li recita, e su Jewish Compass non ci arriva nessuno.
  //
  // Chi chiama questa funzione rimette i campi dentro l'oggetto scheda, cosi'
  // tutti i bottoni gia' scritti (Chiama, WhatsApp, Sito, Email) si accendono
  // da soli senza che nessuno di loro sappia niente di tutto questo.
  revealContacts: (estId) => request(`/api/establishments/${estId}/contacts`),

  trackClick: (estId, action) => {
    try {
      const token = getToken();
      fetch(`${API_URL}/api/establishments/${estId}/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ action }),
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* never break a click */ }
  },
  subtypes: (params = {}) => {
    const qs = buildQuery(params);
    return request(`/api/subtypes${qs ? `?${qs}` : ''}`);
  },
  communities: (params = {}) => {
    const qs = buildQuery(params);
    return request(`/api/communities${qs ? `?${qs}` : ''}`);
  },
  // Who vouches for places — the distinct recommended_by values in use, with
  // counts, so the filter only ever offers a name that has something behind it.
  recommenders: (params = {}) => {
    const qs = buildQuery(params);
    return request(`/api/recommenders${qs ? `?${qs}` : ''}`);
  },
  // The kosher standards in use here — Mehadrin, Lubavitch Shechita, Chalav
  // Israel and the rest, with counts. Scoped like the others, so Afula offers
  // the levels Afula actually has rather than the whole KOSHER_LEVELS list.
  kosherLevels: (params = {}) => {
    const qs = buildQuery(params);
    return request(`/api/kosher-levels${qs ? `?${qs}` : ''}`);
  },
  // Certifier list. Cached in-memory for 5 min — it's used on every place page
  // to draw the hechsher logo, so refetching each navigation caused a visible
  // delay. `withCounts` (the "certifies N places" badge) is the slow variant,
  // requested only by the browse list; the place page omits it (fast).
  kosherCertifications: (withCounts = false, force = false) => {
    const slot = withCounts ? _certCacheCounts : _certCache;
    if (!force && slot.data && Date.now() - slot.at < 300000) return Promise.resolve(slot.data);
    return request(`/api/kosher-certifications${withCounts ? '?counts=1' : ''}`).then((d) => {
      slot.data = d; slot.at = Date.now();
      return d;
    });
  },
  // All places certified by a given cert id — used by the cert's own detail
  // page to show the list of restaurants/stores/etc. it supervises.
  certifiedPlaces: (certId) => request(`/api/kosher-certifications/${certId}/places`),
  // Le altre filiali della stessa catena (stesso file di marchio).
  chainBranches: (id) => request(`/api/establishments/${id}/chain-branches`),
  supervisionsAudit: () => request('/api/admin/supervisions-audit'),
  adminEmailEvents: () => request('/api/admin/email-events'),
  purgeEmail: (email, establishmentId) => request('/api/admin/purge-email', { method: 'POST', body: JSON.stringify({ email, establishmentId }) }),
  emailUsage: (email) => request('/api/admin/email-usage?email=' + encodeURIComponent(email)),
  // Road-trip planner: kosher/Jewish places within buffer_km of the driving
  // route between two {lat,lng} points. Returns { route, count, places }.
  // `filters` carries the same keys the browse list uses — type, subtype,
  // kosher_level, cuisine, community, the flag toggles, include_closed,
  // search — and the endpoint applies them with the list's own SQL, so a
  // road trip and the browse page answer the same question the same way.
  routePlaces: (origin, destination, bufferKm, filters = {}) => {
    const clean = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v != null && v !== false) clean[k] = v; });
    return request('/api/route-places', {
      method: 'POST',
      body: JSON.stringify({ origin, destination, buffer_km: bufferKm, ...clean }),
    });
  },
  supervisions: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.type) qs.set('type', params.type);
    if (params.country) qs.set('country', params.country);
    if (params.city) qs.set('city', params.city);
    const s = qs.toString();
    return request(`/api/supervisions${s ? `?${s}` : ''}`);
  },
  cuisines: (params = {}) => {
    const qs = buildQuery(params);
    return request(`/api/cuisines${qs ? `?${qs}` : ''}`);
  },
  searchSuggest: (q) => request(`/api/search-suggest?q=${encodeURIComponent(q)}`),
  // Geocode a typed address (Nominatim, server-side). Returns { lat, lng }.
  geocode: (q) => request(`/api/geocode?q=${encodeURIComponent(q)}`),
  // Multi-result geocode autocomplete (Nominatim). Returns up to 5 candidates
  // with display_name + lat/lng for the address-search dropdown.
  geocodeSuggest: (q, opts = {}) => {
    const params = new URLSearchParams({ q });
    if (opts.city) params.set('city', opts.city);
    if (opts.country) params.set('country', opts.country);
    return request(`/api/geocode-suggest?${params.toString()}`);
  },
  establishments: (params = {}) => {
    const qs = buildQuery(params);
    return request(`/api/establishments${qs ? `?${qs}` : ''}`);
  },
  establishment: (id) => request(`/api/establishments/${id}`),
  placeBySlug: (cc, city, slug) => request(`/api/places/by-slug/${cc}/${city}/${slug}`),
  // Magic-link conversation access (no login) — visitor replying via emailed link.
  // Review by emailed token — no login. The token names both the place and the
  // reviewer's address, so nothing else needs to be passed or proved.
  reviewByToken: (token) => request(`/api/review/${encodeURIComponent(token)}`),
  submitReviewByToken: (token, review) =>
    request(`/api/review/${encodeURIComponent(token)}`, { method: 'POST', body: JSON.stringify(review) }),
  threadByToken: (token) => request(`/api/thread/${encodeURIComponent(token)}`),
  // Every conversation for the place behind an owner magic-link, so an
  // owner without an account still has an inbox rather than a pile of emails.
  threadInbox: (token) => request(`/api/thread/${encodeURIComponent(token)}/all`),
  replyByToken: (token, reply) =>
    request(`/api/thread/${encodeURIComponent(token)}/reply`, { method: 'POST', body: JSON.stringify({ reply }) }),
  updateEstablishment: (id, body) =>
    request(`/api/establishments/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteEstablishment: (id) =>
    request(`/api/establishments/${id}`, { method: 'DELETE' }),
  // Admin: merge a duplicate (removeId) into the survivor (keepId).
  // Pass { dryRun: true } for a preview of what would change.
  mergePlaces: (keepId, body) =>
    request(`/api/admin/establishments/${keepId}/merge`, { method: 'POST', body: JSON.stringify(body) }),
  // Live parse of prayer-times text for the editor preview (same parser as save)
  previewPrayerTimes: (text) =>
    request(`/api/prayer-times/preview`, { method: 'POST', body: JSON.stringify({ text }) }),
  createEstablishment: (body) =>
    request(`/api/establishments`, { method: 'POST', body: JSON.stringify(body) }),

  // Contact form
  sendContactMessage: (estId, body) =>
    request(`/api/establishments/${estId}/contact`, { method: 'POST', body: JSON.stringify(body) }),
  // Broadcast one request to every place of a category in a city.
  broadcastRequest: (body) =>
    request('/api/broadcast-request', { method: 'POST', body: JSON.stringify(body) }),
  getMessages: (estId) => request(`/api/establishments/${estId}/messages`),
  updateMessage: (messageId, body) =>
    request(`/api/messages/${messageId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteMessage: (messageId) =>
    request(`/api/messages/${messageId}`, { method: 'DELETE' }),
  // The logged-in user's own sent messages + owner replies.
  myMessages: () => request('/api/my-messages'),
  // The logged-in user's reviews (with owner replies).
  myReviews: () => request('/api/my-reviews'),
  // Recent notifications for the header bell (review + message replies).
  myNotifications: () => request('/api/my-notifications'),
  // Editorial description for a country/city landing page (SEO).
  geoDescription: ({ country, city } = {}) => {
    const qs = buildQuery({ country, city });
    return request(`/api/geo-description${qs ? `?${qs}` : ''}`);
  },
  saveGeoDescription: (body) =>
    request('/api/admin/geo-description', { method: 'POST', body: JSON.stringify(body) }),
  // Owner/admin replies to a contact message (appended to the thread + emailed).
  replyToMessage: (messageId, reply) =>
    request(`/api/messages/${messageId}/reply`, { method: 'POST', body: JSON.stringify({ reply }) }),
  // The original sender (logged in) replies back to the owner, continuing the thread.
  userReplyToMessage: (messageId, reply) =>
    request(`/api/messages/${messageId}/user-reply`, { method: 'POST', body: JSON.stringify({ reply }) }),
  // Re-send the original contact email to the establishment's current address
  // (after a wrong/missing email was corrected). Owner/admin.
  resendMessage: (messageId) =>
    request(`/api/messages/${messageId}/resend`, { method: 'POST' }),

  // Saved reply templates (owner "canned responses") + per-listing auto-reply.
  replyTemplates: () => request('/api/reply-templates'),
  createReplyTemplate: (title, body) =>
    request('/api/reply-templates', { method: 'POST', body: JSON.stringify({ title, body }) }),
  deleteReplyTemplate: (id) => request(`/api/reply-templates/${id}`, { method: 'DELETE' }),
  getAutoReply: (estId) => request(`/api/establishments/${estId}/auto-reply`),
  setAutoReply: (estId, enabled, message) =>
    request(`/api/establishments/${estId}/auto-reply`, { method: 'PUT', body: JSON.stringify({ enabled, message }) }),

  // Owner dashboard
  ownerEstablishments: () => request('/api/owner/establishments'),
  ownerMessages: (params = {}) => {
    const qs = buildQuery(params);
    return request(`/api/owner/messages${qs ? `?${qs}` : ''}`);
  },
  ownerStats: () => request('/api/owner/stats'),

  // Admin approval queue
  // What place is this link pointing at? Reads only — the server resolves
  // the link, reads the name and address out of a Google Maps URL and asks
  // OpenStreetMap for the building's telephone and website. Nothing is
  // created; it fills the Add Place form in.
  resolveLink: (url) => request('/api/resolve-link', { method: 'POST', body: JSON.stringify({ url }) }),
  adminPending: () => request('/api/admin/pending'),
  adminActivity: (opts = {}) => request(`/api/admin/activity${opts.hideAdmin ? '?hide_admin=true' : ''}`),
  adminPendingCount: () => request('/api/admin/pending-count'),
  // The hechsherim that have run out and the ones about to. Israeli
  // teudot nearly all run to erev Pesach, so these fall due in one clump
  // a year rather than trickling — `days` widens the warning window.
  adminKashrutExpiry: (days = 30) => request(`/api/admin/kashrut-expiry?days=${encodeURIComponent(days)}`),
  adminOwners: () => request('/api/admin/owners'),
  // Full users list (all roles) + admin-triggered verification resend.
  adminUsers: ({ search = '', unverified = false } = {}) => {
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (unverified) qs.set('unverified', '1');
    const q = qs.toString();
    return request(`/api/admin/users${q ? `?${q}` : ''}`);
  },
  adminResendVerification: (userId) =>
    request(`/api/admin/users/${userId}/resend-verification`, { method: 'POST' }),
  adminDeleteUser: (userId) =>
    request(`/api/admin/users/${userId}`, { method: 'DELETE' }),
  adminUpdateUser: (userId, fields) =>
    request(`/api/admin/users/${userId}`, { method: 'PATCH', body: JSON.stringify(fields) }),

  // Admin diagnostics: SMTP config + optional live test email
  adminHealth: () => request('/api/admin/health'),
  adminHealthTest: () => request('/api/admin/health?test=1'),

  // Community-suggested edits + photos
  suggestEdit: (estId, body) =>
    request(`/api/establishments/${estId}/suggest-edit`, { method: 'POST', body: JSON.stringify(body) }),
  uploadSuggestionImage: async (estId, file) => {
    const form = new FormData();
    form.append('image', file);
    const token = localStorage.getItem('jwt_token');
    const res = await fetch(`${API_URL}/api/establishments/${estId}/suggest-image`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Upload failed');
    return res.json();
  },
  editSuggestions: () => request('/api/edit-suggestions'),
  mySubmissions: () => request('/api/my-submissions'),
  approveEditSuggestion: (id, changes) =>
    request(`/api/edit-suggestions/${id}/approve`, { method: 'POST', body: JSON.stringify(changes ? { changes } : {}) }),
  rejectEditSuggestion: (id, reason) =>
    request(`/api/edit-suggestions/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
  // Inverte un'unione: chi sopravvive e chi viene assorbito si scambiano.
  swapMergeSuggestion: (id) =>
    request(`/api/edit-suggestions/${id}/swap`, { method: 'POST', body: '{}' }),
  adminApprove: (kind, id) =>
    request('/api/admin/approve', { method: 'POST', body: JSON.stringify({ kind, id }) }),
  adminReject: (kind, id) =>
    request('/api/admin/reject', { method: 'POST', body: JSON.stringify({ kind, id }) }),

  // Multi-menu display (named menus per restaurant — Lunch, Dinner, Shabbat …)
  listMenus: (estId) => request(`/api/establishments/${estId}/menus`),
  createMenu: (estId, body) =>
    request(`/api/establishments/${estId}/menus`, { method: 'POST', body: JSON.stringify(body) }),
  updateMenu: (id, body) =>
    request(`/api/menus/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteMenu: (id) => request(`/api/menus/${id}`, { method: 'DELETE' }),
  ocrMenu: async (file, lang) => {
    const form = new FormData();
    form.append('file', file);
    if (lang) form.append('lang', lang);
    const token = localStorage.getItem('jwt_token');
    const res = await fetch(`${API_URL}/api/menus/ocr`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || data.error || 'OCR failed');
    return data;
  },

  // Read a menu from a link the owner already hosts. Errors carry source_url
  // when the file downloaded but yielded no text — the caller keeps the link
  // rather than discarding a perfectly good menu PDF.
  ocrMenuUrl: async (url, lang) => {
    const token = localStorage.getItem('jwt_token');
    const res = await fetch(`${API_URL}/api/menus/ocr-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ url, lang }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.message || data.error || 'Could not read that link');
      if (data.source_url) { err.source_url = data.source_url; err.source_type = data.source_type; }
      throw err;
    }
    return data;
  },

  // Menu + Orders (ordering system — separate from listMenus above)
  menuItems: (estId) => request(`/api/establishments/${estId}/menu-items`),
  createMenuItem: (estId, body) =>
    request(`/api/establishments/${estId}/menu-items`, { method: 'POST', body: JSON.stringify(body) }),
  updateMenuItem: (id, body) =>
    request(`/api/menu-items/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteMenuItem: (id) => request(`/api/menu-items/${id}`, { method: 'DELETE' }),
  placeOrder: (estId, body) =>
    request(`/api/establishments/${estId}/orders`, { method: 'POST', body: JSON.stringify(body) }),
  ownerOrders: () => request('/api/owner/orders'),
  updateOrderStatus: (id, status) =>
    request(`/api/orders/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),

  // Claims
  submitClaim: (estId, message) =>
    request(`/api/establishments/${estId}/claim`, { method: 'POST', body: JSON.stringify({ message }) }),
  myClaim: (estId) => request(`/api/establishments/${estId}/my-claim`),
  adminClaims: () => request('/api/admin/claims'),
  approveClaim: (claimId) =>
    request(`/api/admin/claims/${claimId}/approve`, { method: 'POST' }),
  rejectClaim: (claimId) =>
    request(`/api/admin/claims/${claimId}/reject`, { method: 'POST' }),

  // Bookings
  createBooking: (body) => request('/api/bookings', { method: 'POST', body: JSON.stringify(body) }),

  // Booking requests (client requests a room/tour → owner accepts or rejects)
  createBookingRequest: (estId, body) =>
    request(`/api/establishments/${estId}/booking-requests`, { method: 'POST', body: JSON.stringify(body) }),
  myBookingRequests: () => request('/api/my-booking-requests'),
  cancelBookingRequest: (id) =>
    request(`/api/booking-requests/${id}/cancel`, { method: 'POST' }),
  ownerBookingRequests: () => request('/api/owner/booking-requests'),
  ownerBookingPendingCount: () => request('/api/owner/booking-requests/pending-count'),
  // Owner accepts; optional `edits` ({ total_price, pickup, dropoff, pickup_at,
  // note }) let the owner adjust the trip before confirming (edit-then-confirm).
  acceptBookingRequest: (id, edits) =>
    request(`/api/booking-requests/${id}/accept`, { method: 'POST', body: JSON.stringify(edits || {}) }),
  rejectBookingRequest: (id, note) =>
    request(`/api/booking-requests/${id}/reject`, { method: 'POST', body: JSON.stringify({ note }) }),

  // Driver preset trips (jewish_driver). Public list + owner CRUD.
  driverTrips: (estId) => request(`/api/establishments/${estId}/driver-trips`),
  ownerDriverTrips: () => request('/api/owner/driver-trips'),
  createDriverTrip: (estId, body) =>
    request(`/api/establishments/${estId}/driver-trips`, { method: 'POST', body: JSON.stringify(body) }),
  updateDriverTrip: (id, body) =>
    request(`/api/driver-trips/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteDriverTrip: (id) => request(`/api/driver-trips/${id}`, { method: 'DELETE' }),

  // Inventory — rooms (hotels/accommodations)
  // Places the caller can add inventory to (owner: theirs; admin: all hotel/tour places).
  // Page views — record a visit (fire-and-forget) and read back counts.
  recordView: (estId) => request(`/api/establishments/${estId}/view`, { method: 'POST' }).catch(() => {}),
  // DA DOVE SIAMO ARRIVATI, una volta per sessione.
  //
  // Menachem, 21/10/2026: «puoi fare un controllo chi linka a noi?». Il
  // browser `document.referrer` non lo manda sulle chiamate XHR — lì il
  // referer siamo noi — quindi va passato a mano, e una volta sola: dieci
  // pagine lette di fila sono un arrivo, non dieci.
  recordReferral: () => {
    try {
      const ref = document.referrer;
      if (!ref) return Promise.resolve();
      if (sessionStorage.getItem('jc_ref_sent')) return Promise.resolve();
      sessionStorage.setItem('jc_ref_sent', '1');
      return request('/api/referral', {
        method: 'POST',
        body: JSON.stringify({ ref, path: window.location.pathname + window.location.search }),
      }).catch(() => {});
    } catch (e) { return Promise.resolve(); }   // sessionStorage bloccato
  },
  placeViews: (estId) => request(`/api/establishments/${estId}/views`),
  placeClicks: (estId) => request(`/api/establishments/${estId}/clicks`),
  ownerPlaceViews: () => request('/api/owner/place-views'),
  // Most-visited places / cities in a recent window (home page).
  topVisitedPlaces: (days = 30, limit = 12) => request(`/api/stats/top-places?days=${days}&limit=${limit}`),
  topVisitedCities: (days = 30, limit = 12) => request(`/api/stats/top-cities?days=${days}&limit=${limit}`),
  topContactedPlaces: (days = 30, limit = 12) => request(`/api/stats/top-contacted?days=${days}&limit=${limit}`),

  // News / articles. Public list is published-only; admins may pass status.
  news: (params = {}) => {
    const qs = buildQuery(params);
    return request(`/api/news${qs ? `?${qs}` : ''}`);
  },
  // I cambi di kashrut. Quelli pubblici per chiunque, tutti per l'admin.
  kashrutChanges: (limit = 20) => request(`/api/kashrut-changes?limit=${limit}`),
  adminKashrutChanges: (limit = 200) => request(`/api/admin/kashrut-changes?limit=${limit}`),
  updateKashrutChange: (id, body) => request(`/api/admin/kashrut-changes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteKashrutChange: (id) => request(`/api/admin/kashrut-changes/${id}`, { method: 'DELETE' }),
  newsArticle: (slug) => request(`/api/news/${encodeURIComponent(slug)}`),
  myNews: () => request('/api/news/mine'),
  placeNews: (estId) => request(`/api/establishments/${estId}/news`),
  createNews: (body) => request('/api/news', { method: 'POST', body: JSON.stringify(body) }),
  updateNews: (id, body) => request(`/api/news/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  publishNews: (id) => request(`/api/news/${id}/publish`, { method: 'POST' }),
  unpublishNews: (id) => request(`/api/news/${id}/unpublish`, { method: 'POST' }),
  deleteNews: (id) => request(`/api/news/${id}`, { method: 'DELETE' }),
  // Upload one article image (multipart). Returns { url }. FormData must NOT
  // carry a JSON Content-Type, so this bypasses the `request` helper.
  uploadNewsImage: async (file) => {
    const fd = new FormData();
    fd.append('image', file);
    const token = getToken();
    const res = await fetch(`${API_URL}/api/news/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || data.message || 'Upload failed');
    return data;
  },
  // Upload a country's kosher-list PDF (admin, multipart). Returns { url }.
  uploadKosherListPdf: async (file) => {
    const fd = new FormData();
    fd.append('pdf', file);
    const token = getToken();
    const res = await fetch(`${API_URL}/api/admin/kosher-list-upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || data.message || 'Upload failed');
    return data;
  },

  inventoryPlaces: () => request('/api/inventory/places'),
  rooms: (estId) => request(`/api/establishments/${estId}/rooms`),
  createRoom: (estId, body) =>
    request(`/api/establishments/${estId}/rooms`, { method: 'POST', body: JSON.stringify(body) }),
  updateRoom: (id, body) =>
    request(`/api/rooms/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteRoom: (id) => request(`/api/rooms/${id}`, { method: 'DELETE' }),

  // Per-room blocked dates (owner-managed vacation/maintenance, plus what the
  // booking modal needs to disable date ranges)
  roomBlockedDates: (roomId) => request(`/api/rooms/${roomId}/blocked-dates`),
  createRoomBlock: (roomId, body) =>
    request(`/api/rooms/${roomId}/blocked-dates`, { method: 'POST', body: JSON.stringify(body) }),
  deleteRoomBlock: (id) => request(`/api/blocked-dates/${id}`, { method: 'DELETE' }),

  // Inventory — tour instances (date-based events)
  tourInstances: (estId) => request(`/api/establishments/${estId}/tour-instances`),
  createTourInstance: (estId, body) =>
    request(`/api/establishments/${estId}/tour-instances`, { method: 'POST', body: JSON.stringify(body) }),
  updateTourInstance: (id, body) =>
    request(`/api/tour-instances/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteTourInstance: (id) => request(`/api/tour-instances/${id}`, { method: 'DELETE' }),

  // Site-wide contact (footer form). Public, no auth required.
  siteContact: (body) => request('/api/contact', { method: 'POST', body: JSON.stringify(body) }),

  // Reviews
  reviews: (estId) => request(`/api/establishments/${estId}/reviews`),
  myReview: (estId) => request(`/api/establishments/${estId}/my-review`),
  saveReview: (estId, body) =>
    request(`/api/establishments/${estId}/reviews`, { method: 'POST', body: JSON.stringify(body) }),
  deleteReview: (id) => request(`/api/reviews/${id}`, { method: 'DELETE' }),
  hideReview: (id, reason) =>
    request(`/api/reviews/${id}/hide`, { method: 'POST', body: JSON.stringify({ reason }) }),
  unhideReview: (id) => request(`/api/reviews/${id}/unhide`, { method: 'POST' }),
  // Pre-moderation: a review is invisible until an admin approves it.
  pendingReviews: () => request('/api/reviews/pending'),
  approveReview: (id) => request(`/api/reviews/${id}/approve`, { method: 'POST' }),
  unapproveReview: (id) => request(`/api/reviews/${id}/unapprove`, { method: 'POST' }),
  // Owners dispute rather than hide — the review stays public until an admin rules.
  disputeReview: (id, reason) =>
    request(`/api/reviews/${id}/dispute`, { method: 'POST', body: JSON.stringify({ reason }) }),
  disputedReviews: () => request('/api/reviews/disputed'),
  dismissDispute: (id) => request(`/api/reviews/${id}/dismiss-dispute`, { method: 'POST' }),
  replyToReview: (id, reply) =>
    request(`/api/reviews/${id}/reply`, { method: 'POST', body: JSON.stringify({ reply }) }),

  // Reports / flags
  createReport: (entity_type, entity_id, reason) =>
    request('/api/reports', { method: 'POST', body: JSON.stringify({ entity_type, entity_id, reason }) }),
  adminReports: (status = 'open') => request(`/api/admin/reports?status=${status}`),
  adminReportsPendingCount: () => request('/api/admin/reports/pending-count'),
  adminDismissReport: (id) => request(`/api/admin/reports/${id}/dismiss`, { method: 'POST' }),
  adminActionReport: (id) => request(`/api/admin/reports/${id}/action`, { method: 'POST' }),
  // Download an image (e.g. a logo found by the importer) straight into a place.
  importImageFromUrl: (estId, url, kind = 'main') =>
    request(`/api/establishments/${estId}/images/from-url`, { method: 'POST', body: JSON.stringify({ url, kind }) }),
  // Demote the main image into the gallery (keeps the file, clears main).
  moveMainToGallery: (estId) =>
    request(`/api/establishments/${estId}/images/main-to-gallery`, { method: 'POST' }),
  // Set (or clear, with empty title) the caption/name of one gallery photo.
  setGalleryTitle: (estId, url, title) =>
    request(`/api/establishments/${estId}/gallery-title`, { method: 'PUT', body: JSON.stringify({ url, title }) }),
  adminImportFromUrl: (url) =>
    request('/api/admin/import-from-url', { method: 'POST', body: JSON.stringify({ url }) }),
  // Stamp the jewishcompass.com watermark on one image (admin-only, on-demand —
  // use only for images that are 100% our own property).
  adminWatermarkImage: (url) =>
    request('/api/admin/watermark-image', { method: 'POST', body: JSON.stringify({ url }) }),

  // Admin: manage who owns a place (primary owner + co-owners)
  establishmentOwners: (estId) => request(`/api/admin/establishments/${estId}/owners`),
  addEstablishmentOwner: (estId, email) =>
    request(`/api/admin/establishments/${estId}/owners`, { method: 'POST', body: JSON.stringify({ email }) }),
  removeEstablishmentOwner: (estId, userId) =>
    request(`/api/admin/establishments/${estId}/owners/${userId}`, { method: 'DELETE' }),

  // GDPR self-serve delete
  deleteMyAccount: () => request('/api/auth/me', { method: 'DELETE' }),

  // Favorites
  favoriteIds: () => request('/api/favorites/ids'),
  favorites: () => request('/api/favorites'),
  // One showcased review for the homepage; the server picks it, once a day.
  reviewOfTheDay: () => request('/api/reviews/of-the-day'),
  addFavorite: (estId) => request(`/api/favorites/${estId}`, { method: 'POST' }),
  removeFavorite: (estId) => request(`/api/favorites/${estId}`, { method: 'DELETE' }),
};

// Format slug to readable label: "kosher_restaurant" → "Kosher Restaurant".
// Overrides handle slugs whose auto-formatted version isn't quite right
// (hyphenation, capitalization quirks, abbreviations).
const CATEGORY_LABEL_OVERRIDES = {
  event_planner: 'Wedding & Event Planner',
  shabbat_friendly_accommodation: 'Shabbat-Friendly Accommodation',
  kosher_cruise: 'Kosher Cruises',
  kosher_senior_living: 'Senior Living & Nursing Homes',
  // 'Hotels & Stays' covers what used to be Hotel + Accommodation +
  // Shabbat-Friendly. All three migrated to kosher_hotel; this label is
  // what visitors see everywhere — list cards, breadcrumbs, place pages.
  kosher_hotel: 'Hotels & Stays',
};
// Hebrew category labels (keyed by slug). When the UI language is Hebrew these
// are used instead of the auto-title-cased English.
const CATEGORY_HE = {
  information_point: 'נקודת מידע',
  synagogue_shul: 'בית כנסת',
  mikvah_ritual_bath: 'מקווה',
  eruv: 'עירוב',
  kosher_restaurant: 'מסעדה כשרה',
  kosher_eatery: 'מזנון כשר',
  kosher_bakery: 'מאפייה כשרה',
  kosher_store: 'חנות מזון כשרה',
  kosher_catering: 'קייטרינג כשר',
  cooking_class: 'סדנת בישול',
  shabbat_meal: 'סעודת שבת',
  kosher_event_venue: 'אולם אירועים כשר',
  // Dettato da Menachem il 16/10: la categoria copre anche appartamenti e
  // alloggi, quindi non "מלון כשר" ma il plurale con la לינה.
  kosher_hotel: 'מלונות ולינה',
  kosher_vacation: 'נופש כשר',
  kosher_cruise: 'שייט כשר',
  kosher_travel_planner: 'תכנון טיולים',
  kosher_travel_agency: 'סוכנות נסיעות',
  kosher_certification_hechsher: 'גופי כשרות',
  judaica_store: 'חנות יודאיקה',
  jewish_tour: 'סיור יהודי',
  jewish_attraction: 'אטרקציה יהודית',
  jewish_museum: 'מוזיאון יהודי',
  jewish_cemetery: 'בית עלמין יהודי',
  jewish_school: 'בית ספר יהודי',
  kosher_senior_living: 'דיור מוגן כשר',
  jewish_driver: 'נהג יהודי',
  event_planner: 'מארגן אירועים וחתונות',
  jewish_photographer: 'צלם יהודי',
  jewish_music: 'להקות וזמרים',
  jewish_periodical: 'עיתונות יהודית',
};

export const formatCategoryLabel = (slug) => {
  if (!slug) return '';
  if (getLang() === 'he' && CATEGORY_HE[slug]) return CATEGORY_HE[slug];
  // Route the English label through t() so French/Italian (and any label the
  // Hebrew map misses) translate via the dictionary; unknown labels fall back
  // to the English text unchanged.
  if (CATEGORY_LABEL_OVERRIDES[slug]) return t(CATEGORY_LABEL_OVERRIDES[slug]);
  return t(slug
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' '));
};

// Dropdown-only category label — strips the "Kosher" / "Jewish" prefix that's
// implicit on this site (everything here is kosher / Jewish-relevant), and
// switches Shabbat-friendly accommodation → "Shabbat Friendly Hotels" which
// users find more natural. Used in the category dropdowns on the home and
// list pages ONLY. Badges, headings, breadcrumbs etc. keep formatCategoryLabel
// so they still read "Kosher Restaurant", "Jewish Tour" etc.
const DROPDOWN_LABEL_OVERRIDES = {
  shabbat_friendly_accommodation: 'Shabbat Friendly Hotels',
  kosher_cruise: 'Cruises',
  kosher_senior_living: 'Senior Living',
  // Dropdown variant: same as the long form here — short enough to read.
  kosher_hotel: 'Hotels & Stays',
};
export const formatCategoryLabelShort = (slug) => {
  if (!slug) return '';
  if (getLang() === 'he' && CATEGORY_HE[slug]) return CATEGORY_HE[slug]; // Hebrew is already concise
  if (DROPDOWN_LABEL_OVERRIDES[slug]) return t(DROPDOWN_LABEL_OVERRIDES[slug]);
  // Strip the implicit "Kosher "/"Jewish " prefix from the ENGLISH label first,
  // then translate — the dictionary keys the short English form.
  const full = (CATEGORY_LABEL_OVERRIDES[slug]) || slug
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
  return t(full.replace(/^(Kosher|Jewish)\s+/, ''));
};

// Per-city landmark icons used by the home-page "Top Cities" section.
// Matched case-insensitively. Cities not listed fall back to the generic
// cityscape used in cityIcon().
const CITY_LANDMARKS = {
  // France
  paris: '🗼', lyon: '🍷', marseille: '⛵', nice: '🏖️', strasbourg: '⛪', toulouse: '🌹', bordeaux: '🍷',
  // UK & Ireland
  london: '🎡', manchester: '🏟️', leeds: '🐦', dublin: '🍀',
  // Italy
  rome: '🏛️', milan: '👗', florence: '🎨', venice: '🚤', naples: '🍕', bologna: '🍝', turin: '☕',
  syracuse: '🎭', siracusa: '🎭', trieste: '🏰',
  // Belgium & Netherlands
  antwerp: '💎', brussels: '🍫', amsterdam: '🌷', 'the hague': '⚖️', knokke: '🏖️',
  // Germany
  berlin: '🐻', munich: '🍺', frankfurt: '🏦', hamburg: '⚓', cologne: '⛪',
  // Switzerland & Austria
  zurich: '🏔️', geneva: '⛲', bern: '🐻', basel: '🎭',
  vienna: '🎼', salzburg: '🎵', innsbruck: '⛷️',
  // Spain & Portugal
  madrid: '🌅', barcelona: '⛪', seville: '💃', valencia: '🍊', lisbon: '🚋', porto: '🍷',
  // Eastern Europe
  budapest: '♨️', prague: '🍻', krakow: '🐉', warsaw: '🧜‍♀️',
  // Russia
  moscow: '❄️', 'st. petersburg': '🌉', 'saint petersburg': '🌉',
  // Scandinavia
  copenhagen: '🧜‍♀️', stockholm: '🦌', oslo: '🛶', helsinki: '🌲',
  // Mediterranean / SE Europe
  athens: '🏺', thessaloniki: '🌊', istanbul: '🕌', sofia: '⛪', belgrade: '🏰',
  bucharest: '🌹', zagreb: '🎨', dubrovnik: '🏰',
  // Other Europe
  monaco: '🎰', luxembourg: '🏰', valletta: '⛵', gibraltar: '🐒',
  // Israel
  jerusalem: '✡️', 'tel aviv': '🌊',
  // North America
  montreal: '🍁', toronto: '🗼', 'new york': '🗽', brooklyn: '🌉', 'flushing': '🌐',
  queens: '🌐', 'los angeles': '🎬', 'san francisco': '🚋', pittsburgh: '🏈', miami: '🌴',
  chicago: '🌭', boston: '⚓', philadelphia: '🔔', baltimore: '🦀', lakewood: '📚',
  // Middle East
  dubai: '🌇', 'abu dhabi': '🕌',
};

// Look up a city's landmark icon; falls back to generic cityscape variants.
export const cityIcon = (city) => {
  if (!city) return '🏙️';
  const key = String(city).trim().toLowerCase();
  return CITY_LANDMARKS[key] || '🏙️';
};

// Icon mapping per category
export const categoryIcon = {
  synagogue_shul: '🕍',
  kosher_restaurant: '🍽️',
  kosher_store: '🛒',
  kosher_eatery: '🥯',
  kosher_bakery: '🥖',
  cooking_class: '👨‍🍳',
  jewish_cemetery: '🪦',
  jewish_tour: '🚶',
  jewish_attraction: '🏰',
  mikvah_ritual_bath: '💧',
  eruv: '🧵',
  information_point: 'ℹ️',
  shabbat_meal: '🍷',
  kosher_event_venue: '🎉',
  kosher_certification_hechsher: '🏵️',
  jewish_museum: '🏛️',
  kosher_catering: '🍴',
  judaica_store: '🛍️',
  accommodation: '🏠',
  shabbat_friendly_accommodation: '🛌',
  kosher_vacation: '🏖️',
  kosher_hotel: '🏨',
  jewish_school: '🏫',
  jewish_photographer: '📷',
  jewish_periodical: '📰',
  jewish_music: '🎵',
  jewish_driver: '🚗',
  event_planner: '💍',
  kosher_cruise: '🛳️',
  kosher_travel_planner: '🗺️',
  kosher_travel_agency: '✈️',
  kosher_senior_living: '🏥',
};

// ── Un campo nella lingua di chi guarda ────────────────────────────────────
// Menachem, 26/10/2026, sul Memoriale della Shoah di Milano: «come si puo'
// avere il titolo anche in inglese? Forse serve per ogni campo diverse
// lingue». La scheda porta una colonna `translations` fatta cosi':
//
//   { "name": { "en": "Shoah Memorial of Milan", "he": "אנדרטת השואה..." } }
//
// Se c'e' la traduzione nella lingua corrente si usa quella, se no la
// colonna di sempre. Niente ripieghi fra lingue: il nome originale e'
// sempre una risposta giusta, la traduzione di una traduzione no.
//
// SI USA SOLO DOVE SI MOSTRA, MAI DOVE SI MODIFICA. Il modulo «suggerisci
// una modifica», la rivendica e la cancellazione leggono `establishment.name`
// grezzo: se leggessero il nome tradotto, una modifica proporrebbe di
// sostituire il nome italiano con quello inglese.
export const tradotto = (riga, campo, lingua) => {
  if (!riga) return '';
  const tr = riga.translations;
  const v = tr && tr[campo] && tr[campo][lingua || getLang()];
  return (typeof v === 'string' && v.trim()) ? v : riga[campo];
};
export const nomeVisibile = (riga) => tradotto(riga, 'name');
